import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Routes, Route, Link, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { 
  LayoutDashboard, 
  Package, 
  ShoppingCart, 
  Plus, 
  Edit, 
  Trash2,
  Loader2,
  Users,
  FolderTree,
  TrendingUp,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  Search,
  Mail,
  Calendar,
  Download,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Play,
  Square,
  Tag,
  Copy,
  Percent,
  ExternalLink,
  Activity,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  Settings,
  Puzzle,
  Receipt,
  Truck,
  CreditCard,
  DollarSign,
  Languages,
  MapPin,
  Store,
  AtSign,
  UserCog,
  Save,
  Palette,
  FileText,
  Sparkles,
  Phone,
  Clock,
  Ruler,
  ImageIcon,
  Upload,
  Megaphone,
  Link as LinkIcon,
  Gift,
  Crown,
  Check,
  Send,
  Award,
  Star,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Enhanced Dashboard Component with Charts
const Dashboard = ({ stats, getAuthHeader }) => {
  const [recentOrders, setRecentOrders] = useState([]);
  const [topProducts, setTopProducts] = useState([]);

  useEffect(() => {
    if (stats.recent_orders) {
      setRecentOrders(stats.recent_orders);
    }
  }, [stats]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'shipped': return 'bg-blue-100 text-blue-800';
      case 'delivered': return 'bg-purple-100 text-purple-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  return (
    <div className="space-y-8">
      <h2 className="font-heading font-bold text-2xl">Dashboard Overview</h2>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 text-white rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm mb-1">Total Products</p>
              <p className="text-3xl font-bold">{stats.product_count || 0}</p>
            </div>
            <Package className="h-10 w-10 text-blue-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 text-white rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm mb-1">Total Orders</p>
              <p className="text-3xl font-bold">{stats.order_count || 0}</p>
            </div>
            <ShoppingCart className="h-10 w-10 text-green-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 text-white rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm mb-1">Customers</p>
              <p className="text-3xl font-bold">{stats.user_count || 0}</p>
            </div>
            <Users className="h-10 w-10 text-purple-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-6 text-white rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-100 text-sm mb-1">Revenue</p>
              <p className="text-3xl font-bold">£{(stats.total_revenue || 0).toFixed(2)}</p>
            </div>
            <TrendingUp className="h-10 w-10 text-amber-200" />
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-secondary p-4 rounded-lg">
          <p className="text-sm text-muted-foreground">Pending Orders</p>
          <p className="text-2xl font-bold mt-1">{stats.pending_orders || 0}</p>
        </div>
        <div className="bg-secondary p-4 rounded-lg">
          <p className="text-sm text-muted-foreground">This Month's Revenue</p>
          <p className="text-2xl font-bold mt-1">£{(stats.monthly_revenue || 0).toFixed(2)}</p>
        </div>
        <div className="bg-secondary p-4 rounded-lg">
          <p className="text-sm text-muted-foreground">Avg. Order Value</p>
          <p className="text-2xl font-bold mt-1">£{(stats.avg_order_value || 0).toFixed(2)}</p>
        </div>
      </div>

      {/* Recent Orders */}
      {stats.recent_orders && stats.recent_orders.length > 0 && (
        <div className="bg-white border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border bg-secondary/50">
            <h3 className="font-heading font-bold text-lg">Recent Orders</h3>
          </div>
          <div className="divide-y divide-border">
            {stats.recent_orders.map((order) => (
              <div key={order.id} className="flex items-center justify-between p-4 hover:bg-secondary/30 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <ShoppingCart className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">#{order.id.slice(0, 8).toUpperCase()}</p>
                    <p className="text-sm text-muted-foreground">{order.guest_email || "Registered User"}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold price-font">£{order.total.toFixed(2)}</p>
                  <span className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="p-4 border-t border-border bg-secondary/30">
            <Link to="/admin/orders" className="text-sm text-primary hover:underline">
              View all orders →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced Products Component with Shop-style Edit Panel
const AdminProducts = ({ getAuthHeader }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    category_id: "",
    category_name: "",
    image_url: "",
    stock: "100",
    tags: "",
    has_color_palette: true,
    material_id: "",
    meta_title: "",
    meta_description: "",
    seo_slug: ""
  });
  const [materials, setMaterials] = useState([]);
  const [bulkPublishing, setBulkPublishing] = useState(false);

  // Bulk publish/unpublish all products
  const handleBulkPublishProducts = async (publish) => {
    setBulkPublishing(true);
    try {
      const response = await axios.post(`${API}/products/bulk-publish`, 
        { is_active: publish },
        { headers: getAuthHeader() }
      );
      toast.success(response.data.message || `All products ${publish ? 'published' : 'unpublished'} successfully!`);
      fetchProducts(); // Refresh the list
    } catch (error) {
      console.error("Error bulk publishing:", error);
      toast.error(`Failed to ${publish ? 'publish' : 'unpublish'} products`);
    } finally {
      setBulkPublishing(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const [productsRes, categoriesRes, materialsRes] = await Promise.all([
        axios.get(`${API}/products?limit=1000&include_inactive=true`),
        axios.get(`${API}/categories/all`),
        axios.get(`${API}/materials`)
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setMaterials(materialsRes.data);
      
      // Check if we should auto-edit a product from URL param
      const editProductId = searchParams.get('editProduct');
      if (editProductId) {
        const productToEdit = productsRes.data.find(p => p.id === editProductId);
        if (productToEdit) {
          handleEdit(productToEdit);
          // Clear the URL param
          setSearchParams({});
        }
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  // Toggle individual product publish status
  const toggleProductPublish = async (product) => {
    const newStatus = product.is_active === false ? true : false;
    try {
      await axios.put(`${API}/products/${product.id}`, 
        { is_active: newStatus },
        { headers: getAuthHeader() }
      );
      toast.success(`Product ${newStatus ? 'published' : 'unpublished'} successfully`);
      // Update local state
      setProducts(products.map(p => 
        p.id === product.id ? { ...p, is_active: newStatus } : p
      ));
    } catch (error) {
      console.error("Error toggling product:", error);
      toast.error("Failed to update product status");
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const productData = {
      name: formData.name,
      description: formData.description,
      price: parseFloat(formData.price),
      category_id: formData.category_id,
      category_name: formData.category_name,
      image_url: formData.image_url,
      stock: parseInt(formData.stock),
      tags: formData.tags.split(",").map(t => t.trim()).filter(Boolean),
      has_color_palette: formData.has_color_palette,
      material_id: formData.material_id,
      meta_title: formData.meta_title,
      meta_description: formData.meta_description,
      seo_slug: formData.seo_slug
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData, {
          headers: getAuthHeader()
        });
        toast.success("Product updated successfully");
      } else {
        await axios.post(`${API}/admin/products`, productData, {
          headers: getAuthHeader()
        });
        toast.success("Product created successfully");
      }
      setDialogOpen(false);
      setEditingProduct(null);
      resetFormData();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const resetFormData = () => {
    setFormData({
      name: "", description: "", price: "", category_id: "", category_name: "",
      image_url: "", stock: "100", tags: "", has_color_palette: true,
      material_id: "", meta_title: "", meta_description: "", seo_slug: ""
    });
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || "",
      price: product.price.toString(),
      category_id: product.category_id || "",
      category_name: product.category_name || "",
      image_url: product.image_url || "",
      stock: product.stock.toString(),
      tags: product.tags?.join(", ") || "",
      has_color_palette: product.has_color_palette !== false,
      material_id: product.material_id || "",
      meta_title: product.meta_title || "",
      meta_description: product.meta_description || "",
      seo_slug: product.seo_slug || ""
    });
    setDialogOpen(false);
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
    resetFormData();
  };

  const handleDelete = async (productId) => {
    // Direct delete without confirm (confirm blocked in iframe)
    try {
      console.log("Deleting product:", productId);
      const response = await axios.delete(`${API}/admin/products/${productId}`, {
        headers: getAuthHeader()
      });
      console.log("Delete response:", response.data);
      toast.success("Product deleted successfully!");
      if (editingProduct?.id === productId) {
        setEditingProduct(null);
      }
      fetchProducts();
    } catch (error) {
      console.error("Delete error:", error.response?.data || error.message);
      toast.error("Failed to delete: " + (error.response?.data?.detail || error.message));
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === "all" || product.category_name === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h2 className="font-heading font-bold text-2xl">Products ({filteredProducts.length})</h2>
        <div className="flex flex-wrap gap-2">
          {/* Bulk Publish/Unpublish Buttons */}
          <Button 
            variant="outline"
            className="rounded-full border-red-500 text-red-500 hover:bg-red-50"
            onClick={() => handleBulkPublishProducts(false)}
            disabled={bulkPublishing}
            data-testid="unpublish-all-products-btn"
          >
            {bulkPublishing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <EyeOff className="h-4 w-4 mr-2" />}
            Unpublish All
          </Button>
          <Button 
            variant="outline"
            className="rounded-full border-green-500 text-green-500 hover:bg-green-50"
            onClick={() => handleBulkPublishProducts(true)}
            disabled={bulkPublishing}
            data-testid="publish-all-products-btn"
          >
            {bulkPublishing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
            Publish All
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="rounded-full"
                onClick={() => {
                  setEditingProduct(null);
                  resetFormData();
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Product</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="rounded-lg mt-1"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Price (£) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="rounded-lg mt-1"
                    required
                  />
                </div>
                <div>
                  <Label>Stock *</Label>
                  <Input
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    className="rounded-lg mt-1"
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Category *</Label>
                <Select
                  value={formData.category_name}
                  onValueChange={(value) => setFormData({ ...formData, category_name: value })}
                >
                  <SelectTrigger className="rounded-lg mt-1">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Image URL</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="rounded-lg mt-1"
                  placeholder="https://..."
                />
              </div>
              <Button type="submit" className="w-full rounded-full" disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Create Product
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-full"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-full md:w-48 rounded-full">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {[...new Set(products.map(p => p.category_name).filter(Boolean))].map((cat) => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Shop-style Edit Panel */}
      {editingProduct && (
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-6 shadow-lg" data-testid="shop-style-edit-panel">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500 rounded-lg">
                <Edit className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-gray-900">Edit Product</h3>
                <p className="text-sm text-gray-500">ID: {editingProduct.id}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={handleCancelEdit} className="text-gray-500 hover:text-gray-700">
              <XCircle className="h-5 w-5 mr-1" />
              Cancel
            </Button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Left Column - Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price (£)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stock</label>
                    <input
                      type="number"
                      value={formData.stock}
                      onChange={(e) => setFormData({...formData, stock: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => {
                      const selectedCat = categories.find(c => c.id === e.target.value);
                      setFormData({
                        ...formData, 
                        category_id: e.target.value,
                        category_name: selectedCat?.name || ""
                      });
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                  >
                    <option value="">Select a category...</option>
                    {categories
                      .filter(c => c.parent_id)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                  </select>
                  {formData.category_name && (
                    <p className="text-xs text-gray-500 mt-1">Current: {formData.category_name}</p>
                  )}
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                  <input
                    type="checkbox"
                    id="admin-has-color-palette"
                    checked={formData.has_color_palette}
                    onChange={(e) => setFormData({...formData, has_color_palette: e.target.checked})}
                    className="w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <label htmlFor="admin-has-color-palette" className="flex-1">
                    <span className="block text-sm font-medium text-gray-700">Show Color Palette</span>
                    <span className="block text-xs text-gray-500">Enable HEXIS color selection</span>
                  </label>
                  <Palette className="h-5 w-5 text-gray-400" />
                </div>
                
                {/* Material Dropdown - Next to Show Color Palette */}
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Ruler className="h-4 w-4 text-green-600" />
                    <label className="text-sm font-medium text-green-800">Material</label>
                  </div>
                  <select
                    value={formData.material_id || ""}
                    onChange={(e) => setFormData({...formData, material_id: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-green-300 rounded-lg bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Select Material...</option>
                    {materials.map((mat) => (
                      <option key={mat.id} value={mat.id}>
                        {mat.name} {mat.price_modifier !== 0 && `(${mat.price_modifier >= 0 ? '+' : ''}£${mat.price_modifier.toFixed(2)})`}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-green-600 mt-1">Select material type for this product</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Product Images</label>
                  
                  {/* All Images Gallery with Order Numbers */}
                  {editingProduct ? (
                    <div className="mb-4">
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                        {(() => {
                          // Use images array if available, otherwise fallback to image_url
                          const displayImages = (editingProduct.images && editingProduct.images.length > 0) 
                            ? editingProduct.images 
                            : (editingProduct.image_url ? [editingProduct.image_url] : []);
                          
                          return displayImages.length > 0 ? (
                          <>
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-medium text-gray-700">
                                Image Order ({displayImages.length} images)
                              </span>
                              <span className="text-xs text-orange-600 font-medium">
                                #1 = Main listing image
                              </span>
                            </div>
                            <div className="grid grid-cols-5 gap-3">
                              {displayImages.map((img, idx) => (
                            <div 
                              key={img + idx} 
                              className="relative"
                            >
                              {/* Large Order Number */}
                              <div className={`absolute -top-2 -left-2 z-10 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shadow-md ${idx === 0 ? 'bg-orange-500 text-white' : 'bg-white text-gray-700 border-2 border-gray-300'}`}>
                                {idx + 1}
                              </div>
                              
                              {/* Image */}
                              <div 
                                draggable
                                onDragStart={(e) => {
                                  e.dataTransfer.setData('text/plain', idx.toString());
                                  e.currentTarget.style.opacity = '0.5';
                                }}
                                onDragEnd={(e) => {
                                  e.currentTarget.style.opacity = '1';
                                }}
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onDragLeave={(e) => {
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                                onDrop={async (e) => {
                                  e.preventDefault();
                                  e.currentTarget.style.transform = 'scale(1)';
                                  const fromIdx = parseInt(e.dataTransfer.getData('text/plain'));
                                  const toIdx = idx;
                                  if (fromIdx === toIdx) return;
                                  
                                  const images = [...displayImages];
                                  const [movedItem] = images.splice(fromIdx, 1);
                                  images.splice(toIdx, 0, movedItem);
                                  
                                  try {
                                    await axios.put(
                                      `${API}/products/${editingProduct.id}/images/reorder`,
                                      images,
                                      { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } }
                                    );
                                    setEditingProduct({...editingProduct, images});
                                    setFormData({...formData, image_url: images[0] || ""});
                                    toast.success("Images reordered");
                                  } catch (error) {
                                    toast.error("Failed to reorder images");
                                  }
                                }}
                                className={`aspect-square rounded-lg overflow-hidden cursor-grab active:cursor-grabbing transition-all ${idx === 0 ? 'ring-2 ring-orange-500 ring-offset-2' : 'border-2 border-gray-200 hover:border-gray-400'}`}
                              >
                                <img src={img} alt={`Image ${idx + 1}`} className="w-full h-full object-cover pointer-events-none" />
                              </div>
                              
                              {/* Main badge */}
                              {idx === 0 && (
                                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-[9px] px-2 py-0.5 rounded-full font-semibold shadow">
                                  MAIN
                                </div>
                              )}
                              
                              {/* Delete link below image */}
                              <div className="text-center mt-1">
                                <span 
                                  onClick={async () => {
                                    const confirmDelete = window.confirm("Delete this image?");
                                    if (!confirmDelete) return;
                                    
                                    try {
                                      const response = await axios.delete(
                                        `${API}/products/${editingProduct.id}/images/${idx}`,
                                        { headers: getAuthHeader() }
                                      );
                                      const updated = response.data.images || [];
                                      setEditingProduct({...editingProduct, images: updated, image_url: updated[0] || ""});
                                      setFormData({...formData, image_url: updated[0] || ""});
                                      toast.success("Image deleted");
                                    } catch (error) {
                                      console.error("Failed to delete image:", error);
                                      toast.error("Failed to delete image");
                                    }
                                  }}
                                  className="text-red-500 hover:text-red-700 text-xs cursor-pointer underline"
                                >
                                  Delete
                                </span>
                              </div>
                            </div>
                          ))}
                          
                          {/* Add Image Button */}
                          <label className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-orange-400 cursor-pointer flex flex-col items-center justify-center text-gray-400 hover:text-orange-500 transition-colors bg-white">
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/gif"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file || !editingProduct) return;
                                
                                const formDataUpload = new FormData();
                                formDataUpload.append('file', file);
                                
                                try {
                                  const response = await axios.post(
                                    `${API}/products/${editingProduct.id}/add-image`,
                                    formDataUpload,
                                    {
                                      headers: {
                                        ...getAuthHeader(),
                                        'Content-Type': 'multipart/form-data'
                                      }
                                    }
                                  );
                                  setEditingProduct({...editingProduct, images: response.data.images});
                                  if (!formData.image_url) {
                                    setFormData({...formData, image_url: response.data.image_url});
                                  }
                                  toast.success("Image added");
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || "Failed to add image");
                                }
                              }}
                            />
                            <Plus className="h-8 w-8" />
                            <span className="text-xs mt-1 font-medium">Add Image</span>
                          </label>
                        </div>
                        <p className="text-xs text-gray-500 mt-3 text-center">
                          Drag images to reorder • Hover for controls • Image #1 appears in shop listings
                        </p>
                          </>
                        ) : (
                          <div className="text-center py-4">
                            <ImageIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                            <p className="text-sm text-gray-500 mb-3">No images uploaded yet</p>
                            <label className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-lg cursor-pointer transition-colors">
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file || !editingProduct) return;
                                  
                                  const formDataUpload = new FormData();
                                  formDataUpload.append('file', file);
                                  
                                  try {
                                    const response = await axios.post(
                                      `${API}/products/${editingProduct.id}/add-image`,
                                      formDataUpload,
                                      {
                                        headers: {
                                          ...getAuthHeader(),
                                          'Content-Type': 'multipart/form-data'
                                        }
                                      }
                                    );
                                    setEditingProduct({...editingProduct, images: response.data.images});
                                    setFormData({...formData, image_url: response.data.image_url});
                                    toast.success("Image added");
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || "Failed to add image");
                                  }
                                }}
                              />
                              <Upload className="h-4 w-4" />
                              <span className="text-sm font-medium">Upload First Image</span>
                            </label>
                          </div>
                        );
                        })()}
                      </div>
                    </div>
                  ) : null}
                  
                  {/* Browse File Manager Button */}
                  <div className="flex gap-2">
                    <Link
                      to="/admin/website-images"
                      target="_blank"
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg border border-purple-300 text-purple-700 hover:bg-purple-50 transition-colors"
                    >
                      <ImageIcon className="h-4 w-4 mr-2" />
                      Open File Manager
                      <ExternalLink className="h-3 w-3 ml-2" />
                    </Link>
                  </div>
                  
                  {/* URL Input */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Or enter image URL</label>
                    <input
                      type="text"
                      value={formData.image_url}
                      onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="/uploads/products/..."
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Max 5MB. JPEG, PNG, WebP, GIF supported.</p>
                </div>
              </div>

              {/* Right Column - Google SEO */}
              <div className="space-y-4">
                {/* Google SEO Section */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Google Search Preview
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Page Title <span className="text-gray-400">({formData.meta_title?.length || 0}/60)</span>
                      </label>
                      <input
                        type="text"
                        value={formData.meta_title}
                        onChange={(e) => setFormData({...formData, meta_title: e.target.value})}
                        className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Title shown in Google search results"
                        maxLength={60}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Description <span className="text-gray-400">({formData.meta_description?.length || 0}/160)</span>
                      </label>
                      <textarea
                        value={formData.meta_description}
                        onChange={(e) => setFormData({...formData, meta_description: e.target.value})}
                        rows={2}
                        className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Description shown under the title in Google"
                        maxLength={160}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Search Friendly Page Name
                      </label>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-400 mr-1">/product/</span>
                        <input
                          type="text"
                          value={formData.seo_slug}
                          onChange={(e) => setFormData({...formData, seo_slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')})}
                          className="flex-1 px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="black-matte-vinyl-decal"
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Clean URL for better SEO (auto-formatted)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Description - Full Width */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Product Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={4}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Enter product description..."
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6 pt-4 border-t border-orange-200">
              <Button 
                type="submit" 
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white rounded-full"
                disabled={saving}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                className="rounded-full"
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>
              <Button 
                type="button" 
                variant="ghost" 
                className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full"
                onClick={() => handleDelete(editingProduct.id)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Products Table */}
      <div className="bg-white border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary/50 border-b border-border">
              <tr>
                <th className="text-left p-4 font-medium text-sm">Product</th>
                <th className="text-left p-4 font-medium text-sm">Category</th>
                <th className="text-left p-4 font-medium text-sm">Price</th>
                <th className="text-left p-4 font-medium text-sm">Stock</th>
                <th className="text-left p-4 font-medium text-sm">Status</th>
                <th className="text-right p-4 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredProducts.map((product) => (
                <tr 
                  key={product.id} 
                  className={`hover:bg-secondary/30 transition-colors ${editingProduct?.id === product.id ? 'bg-orange-50 border-l-4 border-l-orange-500' : ''}`}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                        {product.image_url ? (
                          <img src={product.image_url} alt={product.name} className="w-full h-full object-contain" />
                        ) : (
                          <ImageIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{product.name}</p>
                        <p className="text-xs text-muted-foreground">ID: {product.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm bg-secondary px-2 py-1 rounded">
                      {product.category_name || 'Uncategorized'}
                    </span>
                  </td>
                  <td className="p-4 price-font font-bold">£{product.price.toFixed(2)}</td>
                  <td className="p-4">
                    <span className={`text-sm ${product.stock < 10 ? 'text-red-600' : 'text-green-600'}`}>
                      {product.stock} in stock
                    </span>
                  </td>
                  <td className="p-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleProductPublish(product)}
                      className={`rounded-full text-xs font-medium ${
                        product.is_active === false 
                          ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {product.is_active === false ? (
                        <>
                          <EyeOff className="h-3 w-3 mr-1" />
                          Unpublished
                        </>
                      ) : (
                        <>
                          <Eye className="h-3 w-3 mr-1" />
                          Published
                        </>
                      )}
                    </Button>
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant={editingProduct?.id === product.id ? "default" : "ghost"} 
                        size="sm"
                        className={editingProduct?.id === product.id ? "bg-orange-500 hover:bg-orange-600 text-white" : ""}
                        onClick={() => editingProduct?.id === product.id ? handleCancelEdit() : handleEdit(product)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        {editingProduct?.id === product.id ? "Editing" : "Edit"}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(product.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


// Tracking Form Component
const TrackingForm = ({ orderId, getAuthHeader, onUpdate }) => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingCarrier, setTrackingCarrier] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) {
      toast.error("Please enter a tracking number");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.put(
        `${API}/admin/orders/${orderId}/tracking`,
        {
          tracking_number: trackingNumber.trim(),
          tracking_carrier: trackingCarrier.trim(),
          tracking_url: trackingUrl.trim()
        },
        { headers: getAuthHeader() }
      );
      
      if (response.data.email_sent) {
        toast.success("Tracking added & customer notified!");
      } else {
        toast.success("Tracking added (email notification may have failed)");
      }
      
      onUpdate({
        tracking_number: trackingNumber,
        tracking_carrier: trackingCarrier
      });
    } catch (error) {
      toast.error("Failed to update tracking");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label className="text-xs font-medium text-gray-600">Tracking Number *</label>
        <Input
          value={trackingNumber}
          onChange={(e) => setTrackingNumber(e.target.value)}
          placeholder="Enter tracking number"
          className="mt-1"
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-gray-600">Carrier</label>
          <Select value={trackingCarrier} onValueChange={setTrackingCarrier}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select carrier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Royal Mail">Royal Mail</SelectItem>
              <SelectItem value="DPD">DPD</SelectItem>
              <SelectItem value="DHL">DHL</SelectItem>
              <SelectItem value="Evri">Evri (Hermes)</SelectItem>
              <SelectItem value="UPS">UPS</SelectItem>
              <SelectItem value="FedEx">FedEx</SelectItem>
              <SelectItem value="Parcelforce">Parcelforce</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600">Tracking URL (optional)</label>
          <Input
            value={trackingUrl}
            onChange={(e) => setTrackingUrl(e.target.value)}
            placeholder="https://..."
            className="mt-1"
          />
        </div>
      </div>
      <Button 
        type="submit" 
        disabled={loading || !trackingNumber.trim()}
        className="w-full bg-blue-600 hover:bg-blue-700"
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
        ) : (
          <Send className="h-4 w-4 mr-2" />
        )}
        Add Tracking & Notify Customer
      </Button>
    </form>
  );
};

// Enhanced Orders Component
const AdminOrders = ({ getAuthHeader }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const { token } = useAuth();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API}/admin/orders`, {
          headers: getAuthHeader()
        });
        setOrders(response.data);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [getAuthHeader]);

  const updateStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${status}`, {}, {
        headers: getAuthHeader()
      });
      toast.success("Order status updated");
      setOrders(orders.map(o => o.id === orderId ? { ...o, status } : o));
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800 border-green-200';
      case 'shipped': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'delivered': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  const filteredOrders = orders.filter(order => 
    filterStatus === "all" || order.status === filterStatus
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h2 className="font-heading font-bold text-2xl">Orders ({filteredOrders.length})</h2>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full md:w-48 rounded-full">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Orders</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="shipped">Shipped</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {filteredOrders.length === 0 ? (
        <div className="text-center py-12 bg-secondary/30 rounded-lg">
          <ShoppingCart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No orders found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <div key={order.id} className="bg-white border border-border rounded-lg overflow-hidden">
              <div 
                className="flex flex-col md:flex-row md:items-center justify-between p-4 cursor-pointer hover:bg-secondary/30 transition-colors"
                onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
              >
                <div className="flex items-center gap-4">
                  {/* Product thumbnails preview */}
                  <div className="flex -space-x-2">
                    {order.items.slice(0, 3).map((item, idx) => (
                      <div 
                        key={idx}
                        className="w-10 h-10 bg-gray-100 rounded-lg overflow-hidden border-2 border-white flex items-center justify-center"
                      >
                        {item.image_url ? (
                          <img 
                            src={item.image_url} 
                            alt={item.name}
                            className="w-full h-full object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <ImageIcon className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    ))}
                    {order.items.length > 3 && (
                      <div className="w-10 h-10 bg-gray-200 rounded-lg border-2 border-white flex items-center justify-center text-xs font-medium text-gray-600">
                        +{order.items.length - 3}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium">Order #{order.id.slice(0, 8).toUpperCase()}</p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      {new Date(order.created_at).toLocaleDateString()}
                      <Mail className="h-3 w-3 ml-2" />
                      {order.guest_email || "Registered User"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-4 md:mt-0">
                  <span className={`text-xs px-3 py-1 rounded-full border capitalize ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                  <span className="price-font font-bold text-lg">£{order.total.toFixed(2)}</span>
                  {expandedOrder === order.id ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
              </div>
              
              {expandedOrder === order.id && (
                <div className="border-t border-border p-4 bg-secondary/20">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Shipping Address */}
                    <div>
                      <h4 className="font-medium mb-2">Shipping Address</h4>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <p>{order.shipping_address?.name}</p>
                        <p>{order.shipping_address?.address}</p>
                        <p>{order.shipping_address?.city}, {order.shipping_address?.postcode}</p>
                        <p>{order.shipping_address?.country}</p>
                      </div>
                    </div>
                    
                    {/* Order Instructions */}
                    {order.order_instructions && (
                      <div>
                        <h4 className="font-medium mb-2">Add further Instruction to order</h4>
                        <div className="text-sm text-muted-foreground bg-yellow-50 p-3 rounded border border-yellow-200">
                          <p>{order.order_instructions}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Update Status */}
                    <div>
                      <h4 className="font-medium mb-2">Update Status</h4>
                      <Select
                        value={order.status}
                        onValueChange={(value) => updateStatus(order.id, value)}
                      >
                        <SelectTrigger className="w-full rounded-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="dispatched">Dispatched</SelectItem>
                          <SelectItem value="shipped">Shipped</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Tracking Information */}
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Truck className="h-4 w-4 text-blue-600" />
                      Tracking Information
                    </h4>
                    {order.tracking_number ? (
                      <div className="space-y-2">
                        <p className="text-sm"><strong>Tracking #:</strong> {order.tracking_number}</p>
                        {order.tracking_carrier && <p className="text-sm"><strong>Carrier:</strong> {order.tracking_carrier}</p>}
                        {order.tracking_email_sent && (
                          <p className="text-xs text-green-600 flex items-center gap-1">
                            <Check className="h-3 w-3" /> Customer notified
                          </p>
                        )}
                      </div>
                    ) : (
                      <TrackingForm orderId={order.id} getAuthHeader={getAuthHeader} onUpdate={(tracking) => {
                        setOrders(orders.map(o => o.id === order.id ? { 
                          ...o, 
                          tracking_number: tracking.tracking_number,
                          tracking_carrier: tracking.tracking_carrier,
                          tracking_email_sent: true,
                          status: 'dispatched'
                        } : o));
                      }} />
                    )}
                  </div>
                  
                  {/* Order Items */}
                  <div className="mt-6">
                    <h4 className="font-medium mb-2">Order Items</h4>
                    <div className="space-y-2">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 bg-white rounded border border-border">
                          <div className="flex items-center gap-3">
                            {/* Product Image */}
                            <div className="w-14 h-14 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                              {item.image_url ? (
                                <img 
                                  src={item.image_url} 
                                  alt={item.name}
                                  className="w-full h-full object-contain"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <ImageIcon className="h-6 w-6 text-gray-400" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium">{item.name}</p>
                                {item.product_id === "custom-text-sticker" && (
                                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">Custom</span>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                Qty: {item.quantity}
                                {item.selected_color && ` • Color: ${item.selected_color}`}
                              </p>
                              {item.instructions && (
                                <p className="text-sm text-muted-foreground italic mt-1">
                                  Instructions: {item.instructions}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {item.product_id === "custom-text-sticker" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`${API}/custom-sticker/order-pdf/${order.id}/${idx}?token=${token}`, '_blank');
                                }}
                                className="text-xs rounded-full"
                                data-testid={`download-pdf-${order.id}-${idx}`}
                              >
                                <Download className="h-3 w-3 mr-1" />
                                PDF
                              </Button>
                            )}
                            <p className="price-font font-medium">£{(item.price * item.quantity).toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Customers Component
const AdminCustomers = ({ getAuthHeader }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await axios.get(`${API}/admin/customers`, {
          headers: getAuthHeader()
        });
        setCustomers(response.data);
      } catch (error) {
        console.error("Error fetching customers:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, [getAuthHeader]);

  const filteredCustomers = customers.filter(customer =>
    customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h2 className="font-heading font-bold text-2xl">Customers ({filteredCustomers.length})</h2>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 rounded-full"
        />
      </div>

      {filteredCustomers.length === 0 ? (
        <div className="text-center py-12 bg-secondary/30 rounded-lg">
          <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No customers found</p>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary/50 border-b border-border">
                <tr>
                  <th className="text-left p-4 font-medium text-sm">Customer</th>
                  <th className="text-left p-4 font-medium text-sm">Email</th>
                  <th className="text-left p-4 font-medium text-sm">Joined</th>
                  <th className="text-left p-4 font-medium text-sm">Orders</th>
                  <th className="text-left p-4 font-medium text-sm">Total Spent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-secondary/30 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <span className="text-primary font-medium">
                            {customer.name?.charAt(0) || customer.email.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="font-medium">{customer.name || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="p-4 text-muted-foreground">{customer.email}</td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="p-4">
                      <span className="bg-secondary px-2 py-1 rounded text-sm">
                        {customer.order_count || 0} orders
                      </span>
                    </td>
                    <td className="p-4 price-font font-medium">
                      £{(customer.total_spent || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Shop Pages Management Component
const AdminShopPages = ({ getAuthHeader }) => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [movingProduct, setMovingProduct] = useState(null);
  const [targetCategory, setTargetCategory] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [catRes, prodRes] = await Promise.all([
        axios.get(`${API}/categories/all`, { headers: getAuthHeader() }),
        axios.get(`${API}/products?limit=1000`, { headers: getAuthHeader() })
      ]);
      setCategories(catRes.data);
      setProducts(prodRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleMoveProduct = async () => {
    if (!movingProduct || !targetCategory) return;
    
    const targetCat = categories.find(c => c.id === targetCategory);
    if (!targetCat) return;

    try {
      await axios.put(
        `${API}/products/${movingProduct.id}`,
        {
          category_id: targetCategory,
          category_name: targetCat.name,
          subcategory: targetCat.slug
        },
        { headers: getAuthHeader() }
      );
      toast.success(`Moved "${movingProduct.name}" to ${targetCat.name}`);
      setMovingProduct(null);
      setTargetCategory("");
      fetchData();
    } catch (error) {
      toast.error("Failed to move product");
    }
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = !searchQuery || 
      p.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || 
      p.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryProductCount = (catId) => {
    return products.filter(p => p.category_id === catId).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-shop-pages">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Shop Pages Management</h2>
          <p className="text-sm text-muted-foreground">Move products between categories and manage shop structure</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Categories List */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <FolderTree className="h-4 w-4" />
            Categories
          </h3>
          <div className="space-y-1 max-h-[500px] overflow-y-auto">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                !selectedCategory ? "bg-primary text-white" : "hover:bg-gray-100"
              }`}
            >
              All Products ({products.length})
            </button>
            {categories
              .filter(c => c.parent_id) // Only subcategories
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                    selectedCategory === cat.id ? "bg-primary text-white" : "hover:bg-gray-100"
                  }`}
                >
                  {cat.name} ({getCategoryProductCount(cat.id)})
                </button>
              ))}
          </div>
        </div>

        {/* Products List */}
        <div className="md:col-span-2 bg-white border rounded-lg p-4">
          <div className="flex items-center gap-4 mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Package className="h-4 w-4" />
              Products
            </h3>
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {filteredProducts.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No products found</p>
            ) : (
              filteredProducts.slice(0, 50).map(product => (
                <div
                  key={product.id}
                  className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50"
                >
                  <img
                    src={product.image_url || "/placeholder.png"}
                    alt={product.name}
                    className="w-12 h-12 object-cover rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{product.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {product.category_name || "No category"} • £{product.price?.toFixed(2)}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setMovingProduct(product)}
                  >
                    Move
                  </Button>
                </div>
              ))
            )}
            {filteredProducts.length > 50 && (
              <p className="text-center text-sm text-muted-foreground py-2">
                Showing 50 of {filteredProducts.length} products. Use search to find more.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Move Product Dialog */}
      <Dialog open={!!movingProduct} onOpenChange={(open) => !open && setMovingProduct(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move Product to Category</DialogTitle>
          </DialogHeader>
          {movingProduct && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <img
                  src={movingProduct.image_url || "/placeholder.png"}
                  alt={movingProduct.name}
                  className="w-16 h-16 object-cover rounded"
                />
                <div>
                  <p className="font-medium">{movingProduct.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Current: {movingProduct.category_name || "No category"}
                  </p>
                </div>
              </div>
              
              <div>
                <Label>Move to Category</Label>
                <Select value={targetCategory} onValueChange={setTargetCategory}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select target category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories
                      .filter(c => c.parent_id && c.id !== movingProduct.category_id)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setMovingProduct(null)}>
                  Cancel
                </Button>
                <Button onClick={handleMoveProduct} disabled={!targetCategory}>
                  Move Product
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Shop Adverts Component
const AdminShopAdverts = ({ getAuthHeader }) => {
  const [adverts, setAdverts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAdvert, setEditingAdvert] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    image_url: "",
    link_url: "",
    description: "",
    position: "sidebar",
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchAdverts();
  }, []);

  const fetchAdverts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/adverts`, { headers: getAuthHeader() });
      setAdverts(response.data);
    } catch (error) {
      toast.error("Failed to load adverts");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingAdvert) {
        await axios.put(`${API}/adverts/${editingAdvert.id}`, formData, { headers: getAuthHeader() });
        toast.success("Advert updated!");
      } else {
        await axios.post(`${API}/adverts`, formData, { headers: getAuthHeader() });
        toast.success("Advert created!");
      }
      setEditingAdvert(null);
      setFormData({ title: "", image_url: "", link_url: "", description: "", position: "sidebar", is_active: true });
      fetchAdverts();
    } catch (error) {
      toast.error("Failed to save advert");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    console.log("handleDelete called with ID:", id, "Type:", typeof id);
    
    if (!id) {
      console.error("Delete failed: No ID provided");
      toast.error("Cannot delete: Invalid advert ID");
      return;
    }
    
    // Confirm blocked in iframe, proceed directly
    
    try {
      console.log("Sending DELETE request to:", `${API}/adverts/${id}`);
      const response = await axios.delete(`${API}/adverts/${id}`, { headers: getAuthHeader() });
      console.log("Delete response:", response.status, response.data);
      
      // Update state directly to ensure immediate UI update
      setAdverts(prevAdverts => {
        const filtered = prevAdverts.filter(advert => advert.id !== id);
        console.log("Adverts before filter:", prevAdverts.length, "After:", filtered.length);
        return filtered;
      });
      toast.success("Advert deleted");
    } catch (error) {
      console.error("Delete error:", error);
      console.error("Error response:", error.response?.data);
      toast.error("Failed to delete: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }
    
    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const authHeader = getAuthHeader();
      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: { 
          'Authorization': authHeader.Authorization
        }
      });
      
      if (response.data.url) {
        setFormData(prev => ({ ...prev, image_url: response.data.url }));
        toast.success("Image uploaded!");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Upload failed - using preview");
      // Use file reader as fallback
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({ ...prev, image_url: event.target.result }));
      };
      reader.readAsDataURL(file);
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-adverts-page">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-2xl">Shop Adverts</h2>
        <Button 
          onClick={() => {
            setEditingAdvert(null);
            setFormData({ title: "", image_url: "", link_url: "", description: "", position: "sidebar", is_active: true });
          }}
          className="bg-black text-white hover:bg-gray-800"
          data-testid="new-advert-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Advert
        </Button>
      </div>

      {/* Edit/Create Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
        <h3 className="font-semibold text-lg">
          {editingAdvert ? "Edit Advert" : "Create New Advert"}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Title</Label>
            <Input 
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Mini Related, Sidebar Banner"
              data-testid="advert-title-input"
            />
          </div>
          
          <div>
            <Label>Position</Label>
            <select
              value={formData.position}
              onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
              className="w-full h-10 px-3 border border-gray-200 rounded-md"
              data-testid="advert-position-select"
            >
              <option value="sidebar">Sidebar (Mini Related)</option>
              <option value="header">Header Banner</option>
              <option value="footer">Footer Banner</option>
            </select>
          </div>
        </div>

        <div>
          <Label>Image URL</Label>
          <div className="flex gap-2">
            <Input 
              value={formData.image_url}
              onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
              placeholder="https://example.com/image.jpg"
              data-testid="advert-image-url-input"
            />
            <label className={`px-4 py-2 ${uploadingImage ? 'bg-gray-300' : 'bg-gray-100 hover:bg-gray-200'} border rounded-md cursor-pointer flex items-center gap-2`}>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
                disabled={uploadingImage}
              />
              {uploadingImage ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Upload
            </label>
          </div>
        </div>

        {formData.image_url && (
          <div className="relative w-48 h-48 bg-gray-100 rounded-lg border overflow-hidden">
            <img 
              src={formData.image_url} 
              alt="Preview" 
              className="w-full h-full object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}

        <div>
          <Label>Click Redirect URL</Label>
          <div className="flex gap-2 items-center">
            <LinkIcon className="h-4 w-4 text-gray-400" />
            <Input 
              value={formData.link_url}
              onChange={(e) => setFormData(prev => ({ ...prev, link_url: e.target.value }))}
              placeholder="https://example.com/destination"
              data-testid="advert-link-url-input"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Users will be redirected to this URL when they click the image</p>
        </div>

        <div>
          <Label>Description / Notes</Label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Add a description..."
            className="w-full px-3 py-2 border border-gray-200 rounded-md resize-none"
            rows={3}
            data-testid="advert-description-input"
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_active"
            checked={formData.is_active}
            onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
            className="h-4 w-4"
          />
          <Label htmlFor="is_active" className="cursor-pointer">Active (visible on shop)</Label>
        </div>

        <div className="flex gap-2">
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-black text-white hover:bg-gray-800"
            data-testid="save-advert-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {editingAdvert ? "Update Advert" : "Create Advert"}
          </Button>
          {editingAdvert && (
            <Button 
              variant="outline"
              onClick={() => {
                setEditingAdvert(null);
                setFormData({ title: "", image_url: "", link_url: "", description: "", position: "sidebar", is_active: true });
              }}
            >
              Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Existing Adverts List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Preview</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Title</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Position</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {adverts.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  No adverts yet. Create your first one above.
                </td>
              </tr>
            ) : (
              adverts.map((advert) => (
                <tr key={advert.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="w-16 h-16 bg-gray-100 rounded border overflow-hidden">
                      {advert.image_url ? (
                        <img src={advert.image_url} alt="" className="w-full h-full object-contain" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <ImageIcon className="h-6 w-6" />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{advert.title || "Untitled"}</div>
                    {advert.link_url && (
                      <a href={advert.link_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                        <LinkIcon className="h-3 w-3" />
                        {advert.link_url.substring(0, 30)}...
                      </a>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-gray-100 rounded text-xs capitalize">{advert.position}</span>
                  </td>
                  <td className="px-4 py-3">
                    {advert.is_active ? (
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Active</span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingAdvert(advert);
                          setFormData({
                            title: advert.title || "",
                            image_url: advert.image_url || "",
                            link_url: advert.link_url || "",
                            description: advert.description || "",
                            position: advert.position || "sidebar",
                            is_active: advert.is_active ?? true
                          });
                        }}
                        data-testid={`edit-advert-${advert.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDelete(advert.id);
                        }}
                        data-testid={`delete-advert-${advert.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Categories Component
const AdminCategories = ({ getAuthHeader }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [publishingAll, setPublishingAll] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    image_url: ""
  });

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories/all`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Bulk publish/unpublish all products
  const handleBulkPublish = async (publish) => {
    setPublishingAll(true);
    try {
      const response = await axios.post(`${API}/products/bulk-publish`, 
        { is_active: publish },
        { headers: getAuthHeader() }
      );
      toast.success(response.data.message || `All products ${publish ? 'published' : 'unpublished'} successfully!`);
    } catch (error) {
      console.error("Error bulk publishing:", error);
      toast.error(`Failed to ${publish ? 'publish' : 'unpublish'} products`);
    } finally {
      setPublishingAll(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCategory) {
        await axios.put(`${API}/admin/categories/${editingCategory.id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Category updated successfully");
      } else {
        await axios.post(`${API}/admin/categories`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Category created successfully");
      }
      setDialogOpen(false);
      setEditingCategory(null);
      setFormData({ name: "", description: "", image_url: "" });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save category");
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || "",
      image_url: category.image_url || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async (categoryId) => {
    // Confirm blocked in iframe, proceed directly
    
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`, {
        headers: getAuthHeader()
      });
      toast.success("Category deleted");
      fetchCategories();
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h2 className="font-heading font-bold text-2xl">Categories ({categories.length})</h2>
        <div className="flex flex-wrap gap-2">
          {/* Bulk Publish/Unpublish Buttons */}
          <Button 
            variant="outline"
            className="rounded-full border-red-500 text-red-500 hover:bg-red-50"
            onClick={() => handleBulkPublish(false)}
            disabled={publishingAll}
            data-testid="unpublish-all-btn"
          >
            {publishingAll ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <EyeOff className="h-4 w-4 mr-2" />}
            Unpublish All Products
          </Button>
          <Button 
            variant="outline"
            className="rounded-full border-green-500 text-green-500 hover:bg-green-50"
            onClick={() => handleBulkPublish(true)}
            disabled={publishingAll}
            data-testid="publish-all-btn"
          >
            {publishingAll ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
            Publish All Products
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="rounded-full"
                onClick={() => {
                  setEditingCategory(null);
                  setFormData({ name: "", description: "", image_url: "" });
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingCategory ? "Edit Category" : "Add Category"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="rounded-full mt-1"
                  required
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="rounded-full mt-1"
                  rows={3}
                />
              </div>
              <div>
                <Label>Image URL</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="rounded-full mt-1"
                  placeholder="https://..."
                />
              </div>
              <Button type="submit" className="w-full rounded-full">
                {editingCategory ? "Update Category" : "Create Category"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {categories.length === 0 ? (
        <div className="text-center py-12 bg-secondary/30 rounded-lg">
          <FolderTree className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No categories found</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => (
            <div key={category.id} className="bg-white border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium">{category.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {category.description || "No description"}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(category)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(category.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Materials Management Component
const AdminMaterials = ({ getAuthHeader }) => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    price_modifier: 0,
    description: ""
  });

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API}/materials`);
      setMaterials(response.data);
    } catch (error) {
      toast.error("Failed to load materials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMaterials();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMaterial) {
        await axios.patch(`${API}/materials/${editingMaterial.id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Material updated");
      } else {
        await axios.post(`${API}/materials`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Material created");
      }
      setDialogOpen(false);
      setEditingMaterial(null);
      setFormData({ name: "", price_modifier: 0, description: "" });
      fetchMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save material");
    }
  };

  const handleDelete = async (materialId) => {
    try {
      await axios.delete(`${API}/materials/${materialId}`, {
        headers: getAuthHeader()
      });
      toast.success("Material deleted");
      setDeleteId(null);
      fetchMaterials();
    } catch (error) {
      toast.error("Failed to delete material");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-2xl">Materials ({materials.length})</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage product material options with pricing</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="rounded-full"
              onClick={() => {
                setEditingMaterial(null);
                setFormData({ name: "", price_modifier: 0, description: "" });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Material
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingMaterial ? "Edit Material" : "Add Material"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Material Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="rounded-lg mt-1"
                  placeholder="e.g., Black Matte Automotive air release vinyl"
                  required
                />
              </div>
              <div>
                <Label>Price Modifier (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price_modifier}
                  onChange={(e) => setFormData({ ...formData, price_modifier: parseFloat(e.target.value) || 0 })}
                  className="rounded-lg mt-1"
                  placeholder="0.00"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  This amount will be added to the base product price. Use negative values for discounts.
                </p>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="rounded-lg mt-1"
                  rows={2}
                  placeholder="Optional description about this material type"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="rounded-full">
                  {editingMaterial ? "Update" : "Create"} Material
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Materials Table */}
      {materials.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Ruler className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No materials yet. Add your first material to get started.</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-4 font-medium text-sm">Material Name</th>
                <th className="text-left p-4 font-medium text-sm">Price Modifier</th>
                <th className="text-left p-4 font-medium text-sm">Description</th>
                <th className="text-right p-4 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {materials.map((material) => (
                <tr key={material.id} className="hover:bg-gray-50">
                  <td className="p-4 font-medium">{material.name}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-sm font-medium ${
                      material.price_modifier > 0 ? 'bg-green-100 text-green-800' : 
                      material.price_modifier < 0 ? 'bg-red-100 text-red-800' : 
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {material.price_modifier >= 0 ? '+' : ''}£{material.price_modifier.toFixed(2)}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground max-w-xs truncate">
                    {material.description || '-'}
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingMaterial(material);
                          setFormData({
                            name: material.name,
                            price_modifier: material.price_modifier,
                            description: material.description || ""
                          });
                          setDialogOpen(true);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setDeleteId(material.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Material</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this material? Products using this material will have their material cleared.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700"
              onClick={() => handleDelete(deleteId)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Website Images Management Component
const AdminWebsiteImages = ({ getAuthHeader }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("all"); // all, category, carousel, advert, upload, unused
  const [searchTerm, setSearchTerm] = useState("");
  const [copySuccess, setCopySuccess] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [newImageFile, setNewImageFile] = useState(null);
  const [replacing, setReplacing] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [selectedImages, setSelectedImages] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const fetchImages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/website-images`, { headers: getAuthHeader() });
      setImages(res.data.images || []);
    } catch (error) {
      console.error("Failed to fetch images:", error);
      toast.error("Failed to load images");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    let successCount = 0;
    let failCount = 0;

    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        await axios.post(`${API}/admin/website-images/upload`, formData, {
          headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }
        });
        successCount++;
      } catch (error) {
        console.error("Upload failed for:", file.name, error);
        failCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} image${successCount > 1 ? 's' : ''} uploaded successfully`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} image${failCount > 1 ? 's' : ''} failed to upload`);
    }
    
    fetchImages();
    setUploading(false);
  };

  const handleFileInput = (e) => {
    handleUpload(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("image/"));
    handleUpload(files);
  };

  const handleDelete = async (img) => {
    if (!img.deletable) {
      toast.error("This image cannot be deleted from here");
      return;
    }

    try {
      const params = new URLSearchParams({
        url: img.url,
        source: img.source || "",
        source_id: img.source_id || ""
      });
      await axios.delete(`${API}/admin/website-images?${params.toString()}`, {
        headers: getAuthHeader()
      });
      toast.success("Image deleted");
      fetchImages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete image");
    }
  };

  const handleBulkDelete = async () => {
    const deletableImages = selectedImages.filter(id => {
      const img = images.find(i => i.id === id);
      return img?.deletable;
    });

    if (deletableImages.length === 0) {
      toast.error("No deletable images selected");
      return;
    }

    let deleted = 0;
    for (const id of deletableImages) {
      const img = images.find(i => i.id === id);
      if (img) {
        try {
          const params = new URLSearchParams({
            url: img.url,
            source: img.source || "",
            source_id: img.source_id || ""
          });
          await axios.delete(`${API}/admin/website-images?${params.toString()}`, {
            headers: getAuthHeader()
          });
          deleted++;
        } catch (error) {
          console.error("Failed to delete:", img.url);
        }
      }
    }

    toast.success(`${deleted} image${deleted > 1 ? 's' : ''} deleted`);
    setSelectedImages([]);
    fetchImages();
  };

  const handleReplace = async (img) => {
    if (!newImageFile) return;
    
    setReplacing(true);
    const formData = new FormData();
    formData.append("file", newImageFile);
    formData.append("replace_url", img.url);
    formData.append("source", img.source);
    formData.append("source_id", img.source_id || "");

    try {
      await axios.post(`${API}/admin/website-images/replace`, formData, {
        headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }
      });
      toast.success("Image replaced successfully");
      setEditingImage(null);
      setNewImageFile(null);
      fetchImages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to replace image");
    } finally {
      setReplacing(false);
    }
  };

  const copyToClipboard = (url) => {
    const fullUrl = url.startsWith("http") ? url : `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopySuccess(url);
    setTimeout(() => setCopySuccess(null), 2000);
    toast.success("URL copied to clipboard");
  };

  const toggleSelect = (id) => {
    setSelectedImages(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedImages.length === filteredImages.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(filteredImages.map(i => i.id));
    }
  };

  const filteredImages = images.filter(img => {
    const matchesFilter = filter === "all" || img.source === filter;
    const matchesSearch = !searchTerm || 
      img.source_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      img.url?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const stats = {
    total: images.length,
    categories: images.filter(i => i.source === "category").length,
    carousel: images.filter(i => i.source === "carousel").length,
    adverts: images.filter(i => i.source === "advert").length,
    uploads: images.filter(i => i.source === "upload").length,
    unused: images.filter(i => i.source === "unused").length
  };

  const sourceColors = {
    category: "bg-green-500",
    carousel: "bg-blue-500",
    advert: "bg-orange-500",
    upload: "bg-purple-500",
    unused: "bg-gray-500"
  };

  const sourceLabels = {
    category: "Category",
    carousel: "Carousel",
    advert: "Advert",
    upload: "Upload",
    unused: "Unused"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="website-images-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <ImageIcon className="h-6 w-6 text-purple-600" />
            File Manager
          </h2>
          <p className="text-gray-500">Manage website design images (categories, carousel, banners)</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileInput}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={uploading}
              data-testid="upload-input"
            />
            <Button disabled={uploading} className="bg-purple-600 hover:bg-purple-700">
              {uploading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
              ) : (
                <><Upload className="h-4 w-4 mr-2" /> Upload Images</>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Drag & Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
          dragOver 
            ? "border-purple-500 bg-purple-50" 
            : "border-gray-300 bg-gray-50 hover:border-gray-400"
        }`}
      >
        <Upload className={`h-10 w-10 mx-auto mb-3 ${dragOver ? "text-purple-500" : "text-gray-400"}`} />
        <p className="text-sm text-gray-600">
          {dragOver ? "Drop images here..." : "Drag and drop images here, or click Upload button above"}
        </p>
        <p className="text-xs text-gray-400 mt-1">Supports: JPG, PNG, WebP, GIF</p>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-6 gap-3">
        <button 
          onClick={() => setFilter("all")}
          className={`border rounded-lg p-3 text-center transition-all ${filter === "all" ? "ring-2 ring-purple-500 bg-purple-50" : "bg-white hover:bg-gray-50"}`}
        >
          <p className="text-xl font-bold">{stats.total}</p>
          <p className="text-xs text-gray-500">All</p>
        </button>
        <button 
          onClick={() => setFilter("category")}
          className={`border border-green-200 rounded-lg p-3 text-center transition-all ${filter === "category" ? "ring-2 ring-green-500 bg-green-100" : "bg-green-50 hover:bg-green-100"}`}
        >
          <p className="text-xl font-bold text-green-600">{stats.categories}</p>
          <p className="text-xs text-green-600">Categories</p>
        </button>
        <button 
          onClick={() => setFilter("carousel")}
          className={`border border-blue-200 rounded-lg p-3 text-center transition-all ${filter === "carousel" ? "ring-2 ring-blue-500 bg-blue-100" : "bg-blue-50 hover:bg-blue-100"}`}
        >
          <p className="text-xl font-bold text-blue-600">{stats.carousel}</p>
          <p className="text-xs text-blue-600">Carousel</p>
        </button>
        <button 
          onClick={() => setFilter("advert")}
          className={`border border-orange-200 rounded-lg p-3 text-center transition-all ${filter === "advert" ? "ring-2 ring-orange-500 bg-orange-100" : "bg-orange-50 hover:bg-orange-100"}`}
        >
          <p className="text-xl font-bold text-orange-600">{stats.adverts}</p>
          <p className="text-xs text-orange-600">Adverts</p>
        </button>
        <button 
          onClick={() => setFilter("upload")}
          className={`border border-purple-200 rounded-lg p-3 text-center transition-all ${filter === "upload" ? "ring-2 ring-purple-500 bg-purple-100" : "bg-purple-50 hover:bg-purple-100"}`}
        >
          <p className="text-xl font-bold text-purple-600">{stats.uploads}</p>
          <p className="text-xs text-purple-600">Uploads</p>
        </button>
        <button 
          onClick={() => setFilter("unused")}
          className={`border border-gray-200 rounded-lg p-3 text-center transition-all ${filter === "unused" ? "ring-2 ring-gray-500 bg-gray-100" : "bg-gray-50 hover:bg-gray-100"}`}
        >
          <p className="text-xl font-bold text-gray-600">{stats.unused}</p>
          <p className="text-xs text-gray-600">Unused</p>
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-4 items-center justify-between bg-white border rounded-lg p-3">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search images..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full"
              data-testid="search-input"
            />
          </div>
          
          {selectedImages.length > 0 && (
            <div className="flex items-center gap-2 pl-3 border-l">
              <span className="text-sm text-gray-600">{selectedImages.length} selected</span>
              <Button 
                size="sm" 
                variant="destructive" 
                onClick={handleBulkDelete}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => setSelectedImages([])}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={selectAll}
            data-testid="select-all-btn"
          >
            {selectedImages.length === filteredImages.length && filteredImages.length > 0 ? "Deselect All" : "Select All"}
          </Button>
          <div className="flex border rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 ${viewMode === "grid" ? "bg-gray-100" : "hover:bg-gray-50"}`}
              title="Grid view"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 ${viewMode === "list" ? "bg-gray-100" : "hover:bg-gray-50"}`}
              title="List view"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>
          <Button size="sm" variant="outline" onClick={fetchImages}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Grid View */}
      {viewMode === "grid" && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {filteredImages.map((img) => (
            <div
              key={img.id}
              className={`group relative bg-white border rounded-lg overflow-hidden transition-all ${
                selectedImages.includes(img.id) ? "ring-2 ring-purple-500" : "hover:shadow-lg"
              }`}
              data-testid={`image-card-${img.id}`}
            >
              {/* Selection Checkbox */}
              <div className="absolute top-2 right-2 z-10">
                <button
                  onClick={(e) => { e.stopPropagation(); toggleSelect(img.id); }}
                  className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                    selectedImages.includes(img.id)
                      ? "bg-purple-600 border-purple-600 text-white"
                      : "bg-white/80 border-gray-300 opacity-0 group-hover:opacity-100"
                  }`}
                >
                  {selectedImages.includes(img.id) && (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              </div>

              {/* Image */}
              <div 
                className="aspect-square bg-gray-100 cursor-pointer"
                onClick={() => setPreviewImage(img)}
              >
                <img
                  src={img.url}
                  alt={img.source_name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/200?text=Error";
                  }}
                />
              </div>
              
              {/* Overlay with Actions */}
              <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5 p-2">
                <Button
                  size="sm"
                  variant="secondary"
                  className="w-full h-8 text-xs"
                  onClick={(e) => { e.stopPropagation(); copyToClipboard(img.url); }}
                >
                  {copySuccess === img.url ? <CheckCircle className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                  {copySuccess === img.url ? "Copied!" : "Copy URL"}
                </Button>
                
                <Button
                  size="sm"
                  variant="secondary"
                  className="w-full h-8 text-xs"
                  onClick={(e) => { e.stopPropagation(); setPreviewImage(img); }}
                >
                  <Eye className="h-3 w-3 mr-1" /> Preview
                </Button>
                
                {img.editable && (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="w-full h-8 text-xs"
                    onClick={(e) => { e.stopPropagation(); setEditingImage(img); }}
                  >
                    <Edit className="h-3 w-3 mr-1" /> Replace
                  </Button>
                )}
                
                {img.deletable && (
                  <Button
                    size="sm"
                    variant="destructive"
                    className="w-full h-8 text-xs"
                    onClick={(e) => { e.stopPropagation(); handleDelete(img); }}
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> Delete
                  </Button>
                )}
              </div>

              {/* Source Badge */}
              <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] font-medium text-white ${sourceColors[img.source] || 'bg-gray-500'}`}>
                {sourceLabels[img.source] || img.source}
              </div>

              {/* Info */}
              <div className="p-2 border-t bg-white">
                <p className="text-xs font-medium truncate" title={img.source_name}>
                  {img.source_name}
                </p>
                <p className="text-[10px] text-gray-400 truncate" title={img.url}>
                  {img.url.split('/').pop()}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === "list" && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="w-12 p-3">
                  <input
                    type="checkbox"
                    checked={selectedImages.length === filteredImages.length && filteredImages.length > 0}
                    onChange={selectAll}
                    className="rounded"
                  />
                </th>
                <th className="w-16 p-3 text-left text-xs font-medium text-gray-500">Preview</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500">Name</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500">Type</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500">URL</th>
                <th className="w-32 p-3 text-left text-xs font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredImages.map((img) => (
                <tr key={img.id} className={`hover:bg-gray-50 ${selectedImages.includes(img.id) ? "bg-purple-50" : ""}`}>
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selectedImages.includes(img.id)}
                      onChange={() => toggleSelect(img.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="p-3">
                    <img
                      src={img.url}
                      alt=""
                      className="w-12 h-12 object-cover rounded cursor-pointer hover:opacity-80"
                      onClick={() => setPreviewImage(img)}
                      onError={(e) => { e.target.src = "https://via.placeholder.com/48?text=Error"; }}
                    />
                  </td>
                  <td className="p-3">
                    <p className="text-sm font-medium">{img.source_name}</p>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium text-white ${sourceColors[img.source] || 'bg-gray-500'}`}>
                      {sourceLabels[img.source] || img.source}
                    </span>
                  </td>
                  <td className="p-3">
                    <p className="text-xs text-gray-500 truncate max-w-xs" title={img.url}>
                      {img.url}
                    </p>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(img.url)} title="Copy URL">
                        <Copy className="h-4 w-4" />
                      </Button>
                      {img.editable && (
                        <Button size="sm" variant="ghost" onClick={() => setEditingImage(img)} title="Replace">
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      {img.deletable && (
                        <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(img)} title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredImages.length === 0 && (
        <div className="text-center py-12 bg-white border rounded-lg">
          <ImageIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No images found</p>
          <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or upload new images</p>
        </div>
      )}

      {/* Preview Modal */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded text-xs font-medium text-white ${sourceColors[previewImage?.source] || 'bg-gray-500'}`}>
                {sourceLabels[previewImage?.source] || previewImage?.source}
              </span>
              {previewImage?.source_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {previewImage && (
              <>
                <div className="flex justify-center bg-gray-100 rounded-lg p-4">
                  <img
                    src={previewImage.url}
                    alt={previewImage.source_name}
                    className="max-h-[60vh] object-contain rounded"
                  />
                </div>
                <div className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
                  <code className="flex-1 text-xs text-gray-600 truncate">{previewImage.url}</code>
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(previewImage.url)}>
                    <Copy className="h-4 w-4 mr-1" /> Copy URL
                  </Button>
                </div>
                <div className="flex gap-2 justify-end">
                  {previewImage.editable && (
                    <Button variant="outline" onClick={() => { setPreviewImage(null); setEditingImage(previewImage); }}>
                      <Edit className="h-4 w-4 mr-2" /> Replace Image
                    </Button>
                  )}
                  {previewImage.deletable && (
                    <Button variant="destructive" onClick={() => { handleDelete(previewImage); setPreviewImage(null); }}>
                      <Trash2 className="h-4 w-4 mr-2" /> Delete
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Replace Image Dialog */}
      <Dialog open={!!editingImage} onOpenChange={() => { setEditingImage(null); setNewImageFile(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replace Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {editingImage && (
              <>
                <div className="text-sm text-gray-500">
                  Replacing: <span className="font-medium">{editingImage.source_name}</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Current</p>
                    <img
                      src={editingImage.url}
                      alt="Current"
                      className="w-full aspect-square object-cover rounded border"
                    />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">New</p>
                    {newImageFile ? (
                      <img
                        src={URL.createObjectURL(newImageFile)}
                        alt="New"
                        className="w-full aspect-square object-cover rounded border"
                      />
                    ) : (
                      <div className="w-full aspect-square bg-gray-100 rounded border flex items-center justify-center">
                        <p className="text-xs text-gray-400">Select new image</p>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setNewImageFile(e.target.files?.[0] || null)}
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => { setEditingImage(null); setNewImageFile(null); }}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => handleReplace(editingImage)} 
                    disabled={!newImageFile || replacing}
                  >
                    {replacing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Replacing...</> : "Replace Image"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Live Activity Component
const AdminLiveActivity = ({ getAuthHeader }) => {
  const [liveData, setLiveData] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchLiveData = useCallback(async () => {
    try {
      const [liveRes, activityRes] = await Promise.all([
        axios.get(`${API}/analytics/live-visitors`, { headers: getAuthHeader() }),
        axios.get(`${API}/analytics/recent-activity?limit=30`, { headers: getAuthHeader() })
      ]);
      setLiveData(liveRes.data);
      setRecentActivity(activityRes.data.activities || []);
    } catch (error) {
      console.error("Failed to fetch live data:", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchLiveData();
    
    // Auto-refresh every 10 seconds if enabled
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchLiveData, 10000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchLiveData, autoRefresh]);

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "";
    const seconds = Math.floor((new Date() - new Date(timestamp)) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  const getDeviceIcon = (device) => {
    switch (device) {
      case "Mobile": return <Smartphone className="h-4 w-4" />;
      case "Tablet": return <Tablet className="h-4 w-4" />;
      default: return <Monitor className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Calibri, sans-serif' }}>
            Live Store Activity
          </h2>
          <p className="text-sm text-muted-foreground">Real-time visitor tracking</p>
        </div>
        <div className="flex gap-2 items-center">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox 
              checked={autoRefresh} 
              onCheckedChange={setAutoRefresh}
            />
            Auto-refresh
          </label>
          <Button variant="outline" size="sm" onClick={fetchLiveData} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-green-500 text-white rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="h-5 w-5" />
            <span className="text-sm opacity-90">Active Now</span>
          </div>
          <div className="text-3xl font-bold">{liveData?.total_active || 0}</div>
        </div>
        
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1 text-muted-foreground">
            <Monitor className="h-5 w-5" />
            <span className="text-sm">Desktop</span>
          </div>
          <div className="text-2xl font-bold">{liveData?.device_breakdown?.Desktop || 0}</div>
        </div>
        
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1 text-muted-foreground">
            <Smartphone className="h-5 w-5" />
            <span className="text-sm">Mobile</span>
          </div>
          <div className="text-2xl font-bold">{liveData?.device_breakdown?.Mobile || 0}</div>
        </div>
        
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1 text-muted-foreground">
            <Globe className="h-5 w-5" />
            <span className="text-sm">Top Page</span>
          </div>
          <div className="text-lg font-bold truncate">
            {liveData?.top_pages?.[0]?.page || "-"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Visitors */}
        <div className="bg-white border rounded-lg">
          <div className="p-4 border-b">
            <h3 className="font-semibold flex items-center gap-2">
              <Eye className="h-4 w-4 text-green-500" />
              Active Visitors ({liveData?.total_active || 0})
            </h3>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {liveData?.visitors?.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No active visitors right now</p>
                <p className="text-sm mt-1">Visitors will appear here when they browse your store</p>
              </div>
            ) : (
              <div className="divide-y">
                {liveData?.visitors?.map((visitor, idx) => (
                  <div key={idx} className="p-3 hover:bg-gray-50">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        {getDeviceIcon(visitor.device_type)}
                        <span className="font-medium text-sm">{visitor.page_title || visitor.current_page}</span>
                      </div>
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">
                        {formatTimeAgo(visitor.last_activity)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{visitor.browser}</span>
                      <span>{visitor.current_page}</span>
                      <span>{visitor.total_page_views} pages viewed</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity Feed */}
        <div className="bg-white border rounded-lg">
          <div className="p-4 border-b">
            <h3 className="font-semibold flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-500" />
              Recent Activity
            </h3>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {recentActivity.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No recent activity</p>
              </div>
            ) : (
              <div className="divide-y">
                {recentActivity.map((activity, idx) => (
                  <div key={idx} className="p-3 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getDeviceIcon(activity.device)}
                        <span className="text-sm">{activity.title || activity.page}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatTime(activity.timestamp)}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {activity.page} • {activity.browser}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Pages */}
      {liveData?.top_pages?.length > 0 && (
        <div className="mt-6 bg-white border rounded-lg p-4">
          <h3 className="font-semibold mb-4">Most Visited Pages (Active Sessions)</h3>
          <div className="space-y-2">
            {liveData.top_pages.map((page, idx) => (
              <div key={idx} className="flex items-center justify-between py-2">
                <span className="text-sm">{page.page}</span>
                <span className="text-sm font-semibold bg-gray-100 px-3 py-1 rounded">
                  {page.count} visitor{page.count !== 1 ? "s" : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Carousel Management Component
const AdminCarousel = ({ getAuthHeader }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newImageUrl, setNewImageUrl] = useState("");
  const [newImageTitle, setNewImageTitle] = useState("");
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [speed, setSpeed] = useState(4000);
  const [savingSpeed, setSavingSpeed] = useState(false);
  const fileInputRef = React.useRef(null);

  const fetchImages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/carousel/images`);
      setImages(response.data.images || []);
      if (response.data.speed) {
        setSpeed(response.data.speed);
      }
    } catch (error) {
      console.error("Failed to fetch carousel images:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleSaveSpeed = async () => {
    setSavingSpeed(true);
    try {
      await axios.put(`${API}/carousel/settings/speed`, { speed }, {
        headers: getAuthHeader()
      });
      toast.success("Carousel speed updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update speed");
    } finally {
      setSavingSpeed(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', file.name.replace(/\.[^/.]+$/, ""));
        
        await axios.post(`${API}/carousel/images/upload`, formData, {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        });
        successCount++;
      } catch (error) {
        console.error("Failed to upload:", file.name, error);
        errorCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`${successCount} image(s) uploaded successfully!`);
      fetchImages();
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} image(s) failed to upload`);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setUploading(false);
  };

  const handleAddImage = async () => {
    if (!newImageUrl.trim()) {
      toast.error("Please enter an image URL");
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/carousel/images`, {
        image_url: newImageUrl.trim(),
        title: newImageTitle.trim()
      }, {
        headers: getAuthHeader()
      });
      toast.success("Image added to carousel!");
      setNewImageUrl("");
      setNewImageTitle("");
      fetchImages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add image");
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    // Confirm blocked in iframe, proceed directly
    try {
      await axios.delete(`${API}/carousel/images/${imageId}`, {
        headers: getAuthHeader()
      });
      toast.success("Image removed from carousel");
      fetchImages();
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Carousel Manager</h2>
          <p className="text-muted-foreground">Add images from your Facebook gallery to the homepage carousel</p>
        </div>
      </div>

      {/* Speed Control */}
      <div className="bg-white p-6 rounded-lg border border-border">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Carousel Speed
        </h3>
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
          <div className="flex-1 w-full sm:w-auto">
            <Label>Auto-play interval (seconds)</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number"
                min="1"
                max="20"
                step="0.5"
                value={speed / 1000}
                onChange={(e) => setSpeed(Math.round(parseFloat(e.target.value) * 1000))}
                className="w-24"
                data-testid="carousel-speed-input"
              />
              <span className="text-sm text-muted-foreground">seconds</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              How long each image displays before moving to the next (1-20 seconds)
            </p>
          </div>
          <Button onClick={handleSaveSpeed} disabled={savingSpeed} data-testid="save-carousel-speed-btn">
            {savingSpeed ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Speed
          </Button>
        </div>
      </div>

      {/* Upload Images from Computer */}
      <div className="bg-white p-6 rounded-lg border border-border">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Upload Images from Computer
        </h3>
        <div className="space-y-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              onChange={handleFileUpload}
              className="hidden"
              id="carousel-file-upload"
            />
            <label 
              htmlFor="carousel-file-upload" 
              className="cursor-pointer flex flex-col items-center gap-3"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-12 w-12 text-gray-400 animate-spin" />
                  <span className="text-lg font-medium text-gray-600">Uploading...</span>
                </>
              ) : (
                <>
                  <Upload className="h-12 w-12 text-gray-400" />
                  <span className="text-lg font-medium text-gray-600">Click to upload images</span>
                  <span className="text-sm text-gray-500">or drag and drop</span>
                  <span className="text-xs text-gray-400">JPEG, PNG, WebP, GIF (multiple files allowed)</span>
                </>
              )}
            </label>
          </div>
        </div>
      </div>

      {/* Add Image by URL */}
      <div className="bg-white p-6 rounded-lg border border-border">
        <h3 className="font-semibold mb-4">Add Image by URL</h3>
        <div className="space-y-4">
          <div>
            <Label>Image URL</Label>
            <Input
              placeholder="Paste Facebook image URL here..."
              value={newImageUrl}
              onChange={(e) => setNewImageUrl(e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Tip: Right-click an image on Facebook → "Copy image address" and paste here
            </p>
          </div>
          <div>
            <Label>Title (optional)</Label>
            <Input
              placeholder="Image title or description"
              value={newImageTitle}
              onChange={(e) => setNewImageTitle(e.target.value)}
              className="mt-1"
            />
          </div>
          <Button onClick={handleAddImage} disabled={adding}>
            {adding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Add to Carousel
          </Button>
        </div>
      </div>

      {/* Preview */}
      {newImageUrl && (
        <div className="bg-white p-6 rounded-lg border border-border">
          <h3 className="font-semibold mb-4">Preview</h3>
          <img 
            src={newImageUrl} 
            alt="Preview" 
            className="max-h-64 rounded-lg object-cover"
            onError={(e) => e.target.style.display = 'none'}
          />
        </div>
      )}

      {/* Current Images */}
      <div className="bg-white p-6 rounded-lg border border-border">
        <h3 className="font-semibold mb-4">Current Carousel Images ({images.length})</h3>
        {images.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            No images added yet. Add your first image above!
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image, index) => (
              <div key={image._id} className="relative group">
                <img 
                  src={image.url} 
                  alt={image.title || `Carousel image ${index + 1}`}
                  className="w-full h-40 object-cover rounded-lg"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => handleDeleteImage(image._id)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Remove
                  </Button>
                </div>
                <div className="absolute bottom-2 left-2 right-2">
                  <span className="bg-black/70 text-white text-xs px-2 py-1 rounded">
                    #{index + 1}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Video Tutorials Management Component
const AdminVideoTutorials = ({ getAuthHeader }) => {
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newVideoUrl, setNewVideoUrl] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [adding, setAdding] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tutorialToDelete, setTutorialToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [tutorialToEdit, setTutorialToEdit] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchTutorials = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tutorials`);
      setTutorials(response.data.tutorials || []);
    } catch (error) {
      console.error("Failed to fetch tutorials:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTutorials();
  }, [fetchTutorials]);

  const handleAddTutorial = async () => {
    if (!newVideoUrl.trim() || !newTitle.trim()) {
      toast.error("Please enter a video URL and title");
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/tutorials`, {
        video_url: newVideoUrl.trim(),
        title: newTitle.trim(),
        description: newDescription.trim()
      }, {
        headers: getAuthHeader()
      });
      toast.success("Video tutorial added!");
      setNewVideoUrl("");
      setNewTitle("");
      setNewDescription("");
      fetchTutorials();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add tutorial");
    } finally {
      setAdding(false);
    }
  };

  const openEditDialog = (tutorial) => {
    setTutorialToEdit(tutorial);
    setEditTitle(tutorial.title || "");
    setEditDescription(tutorial.description || "");
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!tutorialToEdit || !editTitle.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/tutorials/${tutorialToEdit._id}`, {
        title: editTitle.trim(),
        description: editDescription.trim()
      }, {
        headers: getAuthHeader()
      });
      toast.success("Tutorial updated!");
      fetchTutorials();
      setEditDialogOpen(false);
      setTutorialToEdit(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update tutorial");
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (tutorial) => {
    setTutorialToDelete(tutorial);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!tutorialToDelete) return;
    try {
      await axios.delete(`${API}/tutorials/${tutorialToDelete._id}`, {
        headers: getAuthHeader()
      });
      toast.success("Tutorial deleted");
      fetchTutorials();
    } catch (error) {
      toast.error("Failed to delete tutorial");
    } finally {
      setDeleteDialogOpen(false);
      setTutorialToDelete(null);
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const tutorial = tutorials[index];
    const prevTutorial = tutorials[index - 1];
    try {
      // Swap orders
      await axios.put(`${API}/tutorials/${tutorial._id}`, {
        order: prevTutorial.order
      }, { headers: getAuthHeader() });
      await axios.put(`${API}/tutorials/${prevTutorial._id}`, {
        order: tutorial.order
      }, { headers: getAuthHeader() });
      fetchTutorials();
      toast.success("Tutorial moved up");
    } catch (error) {
      toast.error("Failed to reorder");
    }
  };

  const handleMoveDown = async (index) => {
    if (index === tutorials.length - 1) return;
    const tutorial = tutorials[index];
    const nextTutorial = tutorials[index + 1];
    try {
      // Swap orders
      await axios.put(`${API}/tutorials/${tutorial._id}`, {
        order: nextTutorial.order
      }, { headers: getAuthHeader() });
      await axios.put(`${API}/tutorials/${nextTutorial._id}`, {
        order: tutorial.order
      }, { headers: getAuthHeader() });
      fetchTutorials();
      toast.success("Tutorial moved down");
    } catch (error) {
      toast.error("Failed to reorder");
    }
  };

  // Extract thumbnail for YouTube videos
  const getYouTubeThumbnail = (url) => {
    if (!url) return null;
    let videoId = null;
    if (url.includes("youtu.be/")) {
      videoId = url.split("youtu.be/")[1].split("?")[0];
    } else if (url.includes("v=")) {
      videoId = url.split("v=")[1].split("&")[0];
    } else if (url.includes("youtube.com/embed/")) {
      videoId = url.split("embed/")[1].split("?")[0];
    }
    return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Video Tutorials</h2>
          <p className="text-muted-foreground">Manage how-to-apply video tutorials for the public page</p>
        </div>
        <a 
          href="/how-to-apply" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-sm text-primary hover:underline flex items-center gap-1"
        >
          <ExternalLink className="h-4 w-4" />
          View Public Page
        </a>
      </div>

      {/* Add New Tutorial */}
      <div className="bg-white p-6 rounded-lg border border-border">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Plus className="h-5 w-5" />
          Add New Tutorial
        </h3>
        <div className="space-y-4">
          <div>
            <Label>Video URL *</Label>
            <Input
              placeholder="Paste YouTube or Vimeo URL..."
              value={newVideoUrl}
              onChange={(e) => setNewVideoUrl(e.target.value)}
              className="mt-1"
              data-testid="tutorial-url-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Supports YouTube and Vimeo links
            </p>
          </div>
          <div>
            <Label>Title *</Label>
            <Input
              placeholder="e.g., How to Apply Bonnet Stripes"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="mt-1"
              data-testid="tutorial-title-input"
            />
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Textarea
              placeholder="Brief description of what the tutorial covers..."
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              className="mt-1"
              rows={3}
              data-testid="tutorial-description-input"
            />
          </div>
          <Button onClick={handleAddTutorial} disabled={adding} data-testid="add-tutorial-btn">
            {adding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Add Tutorial
          </Button>
        </div>
      </div>

      {/* Current Tutorials */}
      <div className="bg-white p-6 rounded-lg border border-border">
        <h3 className="font-semibold mb-4">Current Tutorials ({tutorials.length})</h3>
        {tutorials.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            No tutorials added yet. Add your first video tutorial above!
          </p>
        ) : (
          <div className="space-y-4">
            {tutorials.map((tutorial, index) => {
              const thumbnail = getYouTubeThumbnail(tutorial.video_url || tutorial.embed_url);
              return (
                <div 
                  key={tutorial._id} 
                  className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg"
                  data-testid={`tutorial-card-${index}`}
                >
                  <div className="flex-shrink-0 w-32 h-20 bg-gray-200 rounded-lg overflow-hidden">
                    {thumbnail ? (
                      <img src={thumbnail} alt={tutorial.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Play className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-grow min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-grow">
                        <h4 className="font-medium">{tutorial.title}</h4>
                        {tutorial.description && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {tutorial.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {/* Order buttons */}
                        <div className="flex flex-col">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                            data-testid={`move-up-btn-${index}`}
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                            onClick={() => handleMoveDown(index)}
                            disabled={index === tutorials.length - 1}
                            data-testid={`move-down-btn-${index}`}
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => openEditDialog(tutorial)}
                          data-testid={`edit-tutorial-btn-${index}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => openDeleteDialog(tutorial)}
                          data-testid={`delete-tutorial-btn-${index}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="bg-black/10 px-2 py-0.5 rounded">#{index + 1}</span>
                      <span className="truncate">{tutorial.video_url}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Tutorial Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Tutorial</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Tutorial title"
                className="mt-1"
                data-testid="edit-tutorial-title"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Brief description..."
                className="mt-1"
                rows={3}
                data-testid="edit-tutorial-description"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-tutorial-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tutorial</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{tutorialToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Configuration Panel Component
const AdminConfiguration = ({ getAuthHeader }) => {
  const [activeTab, setActiveTab] = useState("settings");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState({
    store_name: "Mini Stripes UK",
    store_email: "ministripesuk@gmail.com",
    store_phone: "+44 7852 885975",
    store_address: "United Kingdom",
    currency: "GBP",
    currency_symbol: "£",
    tax_rate: 20,
    tax_included: true,
    free_shipping_threshold: 50,
    default_shipping_cost: 3.99,
    express_shipping_cost: 7.99,
    international_shipping_cost: 12.99,
  });

  // Widgets state
  const [widgets, setWidgets] = useState({
    show_instagram_feed: true,
    show_facebook_widget: true,
    show_newsletter_popup: false,
    show_live_chat: false,
    show_cookie_banner: true,
    show_announcement_bar: false,
    announcement_text: "",
  });

  // Payment state
  const [payments, setPayments] = useState({
    stripe_enabled: true,
    paypal_enabled: false,
    cod_enabled: false,
    bank_transfer_enabled: false,
  });

  // Shipping zones
  const [shippingZones, setShippingZones] = useState([
    { id: 1, name: "United Kingdom", cost: 3.99, free_above: 50 },
    { id: 2, name: "Europe", cost: 12.99, free_above: 100 },
    { id: 3, name: "Rest of World", cost: 19.99, free_above: 150 },
  ]);

  // Countries
  const [countries, setCountries] = useState([
    { code: "GB", name: "United Kingdom", enabled: true },
    { code: "IE", name: "Ireland", enabled: true },
    { code: "DE", name: "Germany", enabled: true },
    { code: "FR", name: "France", enabled: true },
    { code: "NL", name: "Netherlands", enabled: true },
    { code: "BE", name: "Belgium", enabled: true },
    { code: "US", name: "United States", enabled: false },
    { code: "CA", name: "Canada", enabled: false },
    { code: "AU", name: "Australia", enabled: false },
  ]);

  // Email templates
  const [emailTemplates, setEmailTemplates] = useState([
    { id: "order_confirmation", name: "Order Confirmation", enabled: true },
    { id: "shipping_notification", name: "Shipping Notification", enabled: true },
    { id: "order_delivered", name: "Order Delivered", enabled: true },
    { id: "welcome_email", name: "Welcome Email", enabled: true },
    { id: "password_reset", name: "Password Reset", enabled: true },
    { id: "abandoned_cart", name: "Abandoned Cart Reminder", enabled: false },
  ]);

  // Admin accounts
  const [adminAccounts, setAdminAccounts] = useState([
    { id: 1, name: "Admin", email: "admin@ministripesuk.com", role: "Super Admin", last_login: "2026-02-15" },
  ]);

  // Load settings from backend
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await axios.get(`${API}/config/settings`, {
          headers: getAuthHeader()
        });
        const data = response.data;
        if (data.settings) setSettings(data.settings);
        if (data.widgets) setWidgets(data.widgets);
        if (data.shipping_zones) setShippingZones(data.shipping_zones);
        if (data.countries) setCountries(data.countries);
        if (data.payments) setPayments(data.payments);
      } catch (error) {
        console.error("Failed to load settings:", error);
        // Use defaults on error
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, [getAuthHeader]);

  const configTabs = [
    { id: "settings", label: "General Settings", icon: Settings },
    { id: "widgets", label: "Plugin Widgets", icon: Puzzle },
    { id: "tax", label: "Tax", icon: Receipt },
    { id: "shipping", label: "Shipping", icon: Truck },
    { id: "payment", label: "Payment", icon: CreditCard },
    { id: "currencies", label: "Currencies", icon: DollarSign },
    { id: "languages", label: "Languages", icon: Languages },
    { id: "countries", label: "Countries", icon: MapPin },
    { id: "stores", label: "Stores", icon: Store },
    { id: "email", label: "Email", icon: Mail },
    { id: "accounts", label: "Accounts", icon: UserCog },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/config/settings`, {
        settings,
        widgets,
        shipping_zones: shippingZones,
        countries,
        payments,
      }, {
        headers: getAuthHeader()
      });
      toast.success("Settings saved successfully!");
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "settings":
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Store Name</Label>
                <Input 
                  value={settings.store_name} 
                  onChange={(e) => setSettings({...settings, store_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Store Email</Label>
                <Input 
                  type="email"
                  value={settings.store_email} 
                  onChange={(e) => setSettings({...settings, store_email: e.target.value})}
                />
              </div>
              <div>
                <Label>Store Phone</Label>
                <Input 
                  value={settings.store_phone} 
                  onChange={(e) => setSettings({...settings, store_phone: e.target.value})}
                />
              </div>
              <div>
                <Label>Store Address</Label>
                <Input 
                  value={settings.store_address} 
                  onChange={(e) => setSettings({...settings, store_address: e.target.value})}
                />
              </div>
            </div>
          </div>
        );

      case "widgets":
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground mb-4">Enable or disable frontend widgets and features</p>
            {[
              { key: "show_instagram_feed", label: "Instagram Feed", desc: "Show Instagram carousel on homepage" },
              { key: "show_facebook_widget", label: "Facebook Widget", desc: "Show Facebook page widget" },
              { key: "show_newsletter_popup", label: "Newsletter Popup", desc: "Show email signup popup" },
              { key: "show_live_chat", label: "Live Chat", desc: "Enable live chat widget" },
              { key: "show_cookie_banner", label: "Cookie Banner", desc: "Show GDPR cookie consent banner" },
              { key: "show_announcement_bar", label: "Announcement Bar", desc: "Show promotional banner at top" },
            ].map(item => (
              <div key={item.key} className="flex items-center justify-between py-3 border-b">
                <div>
                  <p className="font-medium">{item.label}</p>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
                <Checkbox 
                  checked={widgets[item.key]} 
                  onCheckedChange={(v) => setWidgets({...widgets, [item.key]: v})}
                />
              </div>
            ))}
            {widgets.show_announcement_bar && (
              <div className="mt-4">
                <Label>Announcement Text</Label>
                <Input 
                  value={widgets.announcement_text}
                  onChange={(e) => setWidgets({...widgets, announcement_text: e.target.value})}
                  placeholder="Free shipping on orders over £50!"
                />
              </div>
            )}
          </div>
        );

      case "tax":
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Tax Rate (%)</Label>
                <Input 
                  type="number"
                  value={settings.tax_rate} 
                  onChange={(e) => setSettings({...settings, tax_rate: parseFloat(e.target.value)})}
                />
              </div>
              <div className="flex items-center gap-4 pt-6">
                <Checkbox 
                  checked={settings.tax_included}
                  onCheckedChange={(v) => setSettings({...settings, tax_included: v})}
                />
                <div>
                  <Label>Prices Include Tax</Label>
                  <p className="text-sm text-muted-foreground">Display prices with tax included</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium mb-2">Tax Calculation Preview</h4>
              <p className="text-sm text-muted-foreground">
                Product price: £10.00 → With {settings.tax_rate}% VAT: £{(10 * (1 + settings.tax_rate/100)).toFixed(2)}
              </p>
            </div>
          </div>
        );

      case "shipping":
        return (
          <div className="space-y-6">
            {/* Global Shipping Settings */}
            <div className="bg-white border rounded-lg p-6">
              <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Global Shipping Settings
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Standard Shipping (£)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={settings.default_shipping_cost} 
                    onChange={(e) => setSettings({...settings, default_shipping_cost: parseFloat(e.target.value) || 0})}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Default UK shipping rate</p>
                </div>
                <div>
                  <Label>Express Shipping (£)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={settings.express_shipping_cost} 
                    onChange={(e) => setSettings({...settings, express_shipping_cost: parseFloat(e.target.value) || 0})}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Next-day delivery rate</p>
                </div>
                <div>
                  <Label>International Shipping (£)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={settings.international_shipping_cost} 
                    onChange={(e) => setSettings({...settings, international_shipping_cost: parseFloat(e.target.value) || 0})}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Outside UK shipping rate</p>
                </div>
              </div>
            </div>

            {/* Shipping Zones */}
            <div className="bg-white border rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-lg flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Shipping Zones
                </h4>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="gap-2"
                  onClick={() => {
                    const newId = Math.max(...shippingZones.map(z => z.id), 0) + 1;
                    setShippingZones([...shippingZones, { id: newId, name: "New Zone", cost: 9.99, free_above: 75, enabled: true, delivery_days: "5-7" }]);
                  }}
                >
                  <Plus className="h-4 w-4" /> Add Zone
                </Button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 font-medium text-sm">Zone Name</th>
                      <th className="text-left py-2 px-3 font-medium text-sm">Shipping Cost (£)</th>
                      <th className="text-left py-2 px-3 font-medium text-sm">Free Above (£)</th>
                      <th className="text-left py-2 px-3 font-medium text-sm">Delivery Time</th>
                      <th className="text-left py-2 px-3 font-medium text-sm">Enabled</th>
                      <th className="text-left py-2 px-3 font-medium text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shippingZones.map((zone, idx) => (
                      <tr key={zone.id} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-3">
                          <Input 
                            value={zone.name}
                            onChange={(e) => {
                              const updated = [...shippingZones];
                              updated[idx].name = e.target.value;
                              setShippingZones(updated);
                            }}
                            className="w-40"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <Input 
                            type="number"
                            step="0.01"
                            value={zone.cost}
                            onChange={(e) => {
                              const updated = [...shippingZones];
                              updated[idx].cost = parseFloat(e.target.value) || 0;
                              setShippingZones(updated);
                            }}
                            className="w-24"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <Input 
                            type="number"
                            step="0.01"
                            value={zone.free_above}
                            onChange={(e) => {
                              const updated = [...shippingZones];
                              updated[idx].free_above = parseFloat(e.target.value) || 0;
                              setShippingZones(updated);
                            }}
                            className="w-24"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <Input 
                            value={zone.delivery_days || "3-5"}
                            onChange={(e) => {
                              const updated = [...shippingZones];
                              updated[idx].delivery_days = e.target.value;
                              setShippingZones(updated);
                            }}
                            placeholder="e.g. 3-5 days"
                            className="w-28"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <Checkbox 
                            checked={zone.enabled !== false}
                            onCheckedChange={(v) => {
                              const updated = [...shippingZones];
                              updated[idx].enabled = v;
                              setShippingZones(updated);
                            }}
                          />
                        </td>
                        <td className="py-2 px-3">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              if (shippingZones.length > 1) {
                                setShippingZones(shippingZones.filter((_, i) => i !== idx));
                              } else {
                                toast.error("Must have at least one shipping zone");
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Shipping Services */}
            <div className="bg-white border rounded-lg p-6">
              <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Shipping Services
              </h4>
              <div className="space-y-3">
                {[
                  { id: "standard", name: "Standard Delivery", desc: "3-5 business days", costKey: "default_shipping_cost" },
                  { id: "express", name: "Express Delivery", desc: "1-2 business days", costKey: "express_shipping_cost" },
                  { id: "international", name: "International Delivery", desc: "7-14 business days", costKey: "international_shipping_cost" },
                ].map(service => (
                  <div key={service.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <Checkbox 
                        checked={settings[`${service.id}_enabled`] !== false}
                        onCheckedChange={(v) => setSettings({...settings, [`${service.id}_enabled`]: v})}
                      />
                      <div>
                        <p className="font-medium">{service.name}</p>
                        <p className="text-sm text-muted-foreground">{service.desc}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-lg">£{settings[service.costKey]?.toFixed(2) || "0.00"}</p>
                      <p className="text-xs text-muted-foreground">per order</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button onClick={handleSave} className="gap-2">
                <Save className="h-4 w-4" />
                Save Shipping Settings
              </Button>
            </div>
          </div>
        );

      case "payment":
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground mb-4">Configure payment methods</p>
            {[
              { key: "stripe_enabled", label: "Stripe", desc: "Credit/Debit cards via Stripe", status: "Connected" },
              { key: "paypal_enabled", label: "PayPal", desc: "PayPal payments", status: "Not configured" },
              { key: "cod_enabled", label: "Cash on Delivery", desc: "Pay when order arrives", status: "Available" },
              { key: "bank_transfer_enabled", label: "Bank Transfer", desc: "Direct bank payment", status: "Available" },
            ].map(item => (
              <div key={item.key} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  <Checkbox 
                    checked={payments[item.key]} 
                    onCheckedChange={(v) => setPayments({...payments, [item.key]: v})}
                  />
                  <div>
                    <p className="font-medium">{item.label}</p>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${
                  item.status === "Connected" ? "bg-green-100 text-green-700" :
                  item.status === "Not configured" ? "bg-yellow-100 text-yellow-700" :
                  "bg-gray-100 text-gray-700"
                }`}>
                  {item.status}
                </span>
              </div>
            ))}
          </div>
        );

      case "currencies":
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Primary Currency</Label>
                <Select value={settings.currency} onValueChange={(v) => setSettings({...settings, currency: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GBP">GBP - British Pound (£)</SelectItem>
                    <SelectItem value="EUR">EUR - Euro (€)</SelectItem>
                    <SelectItem value="USD">USD - US Dollar ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Currency Symbol</Label>
                <Input 
                  value={settings.currency_symbol}
                  onChange={(e) => setSettings({...settings, currency_symbol: e.target.value})}
                />
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> Multi-currency support coming soon. Currently all prices are displayed in {settings.currency}.
              </p>
            </div>
          </div>
        );

      case "languages":
        return (
          <div className="space-y-6">
            <div>
              <Label>Primary Language</Label>
              <Select defaultValue="en">
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English (UK)</SelectItem>
                  <SelectItem value="en-us">English (US)</SelectItem>
                  <SelectItem value="de">German</SelectItem>
                  <SelectItem value="fr">French</SelectItem>
                  <SelectItem value="es">Spanish</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> Multi-language support coming soon. Store currently operates in English.
              </p>
            </div>
          </div>
        );

      case "countries":
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground mb-4">Enable countries for shipping and checkout</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {countries.map((country, idx) => (
                <div key={country.code} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{country.code === "GB" ? "🇬🇧" : country.code === "IE" ? "🇮🇪" : country.code === "DE" ? "🇩🇪" : country.code === "FR" ? "🇫🇷" : country.code === "NL" ? "🇳🇱" : country.code === "BE" ? "🇧🇪" : country.code === "US" ? "🇺🇸" : country.code === "CA" ? "🇨🇦" : "🇦🇺"}</span>
                    <span className="font-medium">{country.name}</span>
                  </div>
                  <Checkbox 
                    checked={country.enabled}
                    onCheckedChange={(v) => {
                      const updated = [...countries];
                      updated[idx].enabled = v;
                      setCountries(updated);
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        );

      case "stores":
        return (
          <div className="space-y-6">
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-medium">Mini Stripes UK</h4>
                  <p className="text-sm text-muted-foreground">Primary Store</p>
                </div>
                <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded">Active</span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Domain</p>
                  <p>ministripesuk.com</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Currency</p>
                  <p>GBP (£)</p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> Multi-store support coming soon. Contact support for additional store setup.
              </p>
            </div>
          </div>
        );

      case "email":
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground mb-4">Configure email notifications</p>
            {emailTemplates.map((template, idx) => (
              <div key={template.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  <Checkbox 
                    checked={template.enabled}
                    onCheckedChange={(v) => {
                      const updated = [...emailTemplates];
                      updated[idx].enabled = v;
                      setEmailTemplates(updated);
                    }}
                  />
                  <div>
                    <p className="font-medium">{template.name}</p>
                    <p className="text-sm text-muted-foreground">Template ID: {template.id}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm">Edit Template</Button>
              </div>
            ))}
          </div>
        );

      case "accounts":
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">Manage admin accounts</p>
              <Button size="sm" className="gap-2">
                <Plus className="h-4 w-4" /> Add Admin
              </Button>
            </div>
            {adminAccounts.map(account => (
              <div key={account.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                    <UserCog className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium">{account.name}</p>
                    <p className="text-sm text-muted-foreground">{account.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded">{account.role}</span>
                  <span className="text-xs text-muted-foreground">Last login: {account.last_login}</span>
                  <Button variant="ghost" size="sm">
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Calibri, sans-serif' }}>
            Configuration
          </h2>
          <p className="text-sm text-muted-foreground">Manage your store settings</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Changes
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Config Tabs */}
        <div className="lg:w-56 shrink-0">
          <nav className="bg-white border rounded-lg overflow-hidden">
            {configTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b last:border-b-0 transition-colors ${
                  activeTab === tab.id 
                    ? "bg-primary text-primary-foreground" 
                    : "hover:bg-secondary"
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="flex-1 bg-white border rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            {configTabs.find(t => t.id === activeTab)?.icon && 
              React.createElement(configTabs.find(t => t.id === activeTab).icon, { className: "h-5 w-5" })}
            {configTabs.find(t => t.id === activeTab)?.label}
          </h3>
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

// SEO Manager Component
const AdminSEO = ({ getAuthHeader }) => {
  const [stats, setStats] = useState({ total_products: 0, with_seo: 0, without_seo: 0, coverage_percent: 0 });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({});
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [editProduct, setEditProduct] = useState(null);
  const [editSeo, setEditSeo] = useState({ meta_title: "", meta_description: "", meta_keywords: "", seo_slug: "" });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all"); // all, with_seo, without_seo

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/seo/stats`, { headers: getAuthHeader() });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch SEO stats:", error);
    }
  }, [getAuthHeader]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products?limit=100`);
      let filtered = response.data;
      if (filter === "without_seo") {
        filtered = response.data.filter(p => !p.meta_title);
      } else if (filter === "with_seo") {
        filtered = response.data.filter(p => p.meta_title);
      }
      setProducts(filtered);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchStats();
    fetchProducts();
  }, [fetchStats, fetchProducts]);

  const handleGenerateSEO = async (productId) => {
    setGenerating(prev => ({ ...prev, [productId]: true }));
    try {
      const response = await axios.post(`${API}/seo/generate/${productId}`, {}, { headers: getAuthHeader() });
      if (response.data.success) {
        toast.success("SEO generated successfully!");
        fetchProducts();
        fetchStats();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate SEO");
    } finally {
      setGenerating(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handleBulkGenerate = async () => {
    if (selectedProducts.length === 0) {
      toast.error("Please select products first");
      return;
    }
    setBulkGenerating(true);
    try {
      const response = await axios.post(`${API}/seo/generate-bulk`, selectedProducts, { headers: getAuthHeader() });
      toast.success(`Generated SEO for ${response.data.success.length} products`);
      if (response.data.failed.length > 0) {
        toast.error(`Failed for ${response.data.failed.length} products`);
      }
      setSelectedProducts([]);
      fetchProducts();
      fetchStats();
    } catch (error) {
      toast.error("Bulk generation failed");
    } finally {
      setBulkGenerating(false);
    }
  };

  const handleGenerateAllMissing = async () => {
    const productsWithoutSeo = products.filter(p => !p.meta_title).map(p => p.id);
    if (productsWithoutSeo.length === 0) {
      toast.info("All products have SEO content");
      return;
    }
    setSelectedProducts(productsWithoutSeo.slice(0, 50));
    setBulkGenerating(true);
    try {
      const response = await axios.post(`${API}/seo/generate-bulk`, productsWithoutSeo.slice(0, 50), { headers: getAuthHeader() });
      toast.success(`Generated SEO for ${response.data.success.length} products`);
      fetchProducts();
      fetchStats();
    } catch (error) {
      toast.error("Bulk generation failed");
    } finally {
      setBulkGenerating(false);
      setSelectedProducts([]);
    }
  };

  const handleEditSEO = (product) => {
    setEditProduct(product);
    setEditSeo({
      meta_title: product.meta_title || "",
      meta_description: product.meta_description || "",
      meta_keywords: product.meta_keywords || "",
      seo_slug: product.seo_slug || ""
    });
  };

  const handleSaveSEO = async () => {
    if (!editProduct) return;
    setSaving(true);
    try {
      await axios.put(`${API}/seo/product/${editProduct.id}`, editSeo, { headers: getAuthHeader() });
      toast.success("SEO saved successfully!");
      setEditProduct(null);
      fetchProducts();
      fetchStats();
    } catch (error) {
      toast.error("Failed to save SEO");
    } finally {
      setSaving(false);
    }
  };

  const toggleSelectProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const selectAllVisible = () => {
    const visibleIds = products.map(p => p.id);
    setSelectedProducts(prev => {
      const allSelected = visibleIds.every(id => prev.includes(id));
      return allSelected ? [] : visibleIds;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">SEO Manager</h2>
          <p className="text-muted-foreground">Optimize your products for search engines with AI-powered SEO</p>
        </div>
        <a 
          href="/api/sitemap.xml" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-sm text-primary hover:underline flex items-center gap-1"
        >
          <FileText className="h-4 w-4" />
          View Sitemap
        </a>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border border-border">
          <p className="text-sm text-muted-foreground">Total Products</p>
          <p className="text-2xl font-bold">{stats.total_products}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-border">
          <p className="text-sm text-muted-foreground">With SEO</p>
          <p className="text-2xl font-bold text-green-600">{stats.with_seo}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-border">
          <p className="text-sm text-muted-foreground">Missing SEO</p>
          <p className="text-2xl font-bold text-red-600">{stats.without_seo}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-border">
          <p className="text-sm text-muted-foreground">Coverage</p>
          <p className="text-2xl font-bold">{stats.coverage_percent}%</p>
        </div>
      </div>

      {/* Bulk Actions */}
      <div className="bg-white p-4 rounded-lg border border-border">
        <div className="flex flex-wrap items-center gap-4">
          <Button 
            onClick={handleGenerateAllMissing}
            disabled={bulkGenerating}
            className="bg-green-600 hover:bg-green-700"
          >
            {bulkGenerating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Generate All Missing SEO
          </Button>
          <Button 
            onClick={handleBulkGenerate}
            disabled={bulkGenerating || selectedProducts.length === 0}
            variant="outline"
          >
            {bulkGenerating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Generate Selected ({selectedProducts.length})
          </Button>
          <div className="flex items-center gap-2 ml-auto">
            <Label>Filter:</Label>
            <select 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="all">All Products</option>
              <option value="without_seo">Missing SEO</option>
              <option value="with_seo">Has SEO</option>
            </select>
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="bg-white rounded-lg border border-border overflow-hidden">
        <div className="p-4 border-b border-border flex items-center gap-4">
          <Checkbox 
            checked={products.length > 0 && products.every(p => selectedProducts.includes(p.id))}
            onCheckedChange={selectAllVisible}
          />
          <span className="text-sm text-muted-foreground">
            {loading ? "Loading..." : `${products.length} products`}
          </span>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          </div>
        ) : (
          <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
            {products.map((product) => (
              <div key={product.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start gap-4">
                  <Checkbox 
                    checked={selectedProducts.includes(product.id)}
                    onCheckedChange={() => toggleSelectProduct(product.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium truncate">{product.name}</h4>
                      {product.meta_title ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">SEO ✓</span>
                      ) : (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">No SEO</span>
                      )}
                    </div>
                    {product.meta_title && (
                      <div className="text-xs text-muted-foreground space-y-1 mt-2">
                        <p><strong>Title:</strong> {product.meta_title}</p>
                        <p className="truncate"><strong>Description:</strong> {product.meta_description}</p>
                        <p><strong>Keywords:</strong> {product.meta_keywords?.slice(0, 50)}...</p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleEditSEO(product)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm"
                      onClick={() => handleGenerateSEO(product.id)}
                      disabled={generating[product.id]}
                    >
                      {generating[product.id] ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit SEO Dialog */}
      <Dialog open={!!editProduct} onOpenChange={() => setEditProduct(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit SEO - {editProduct?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Meta Title <span className="text-muted-foreground text-xs">({editSeo.meta_title.length}/60 chars)</span></Label>
              <Input
                value={editSeo.meta_title}
                onChange={(e) => setEditSeo(prev => ({ ...prev, meta_title: e.target.value }))}
                placeholder="SEO-optimized title (50-60 chars)"
                maxLength={70}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Meta Description <span className="text-muted-foreground text-xs">({editSeo.meta_description.length}/160 chars)</span></Label>
              <Textarea
                value={editSeo.meta_description}
                onChange={(e) => setEditSeo(prev => ({ ...prev, meta_description: e.target.value }))}
                placeholder="Compelling description with call-to-action (150-160 chars)"
                maxLength={170}
                rows={3}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Meta Keywords</Label>
              <Input
                value={editSeo.meta_keywords}
                onChange={(e) => setEditSeo(prev => ({ ...prev, meta_keywords: e.target.value }))}
                placeholder="keyword1, keyword2, keyword3"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Comma-separated keywords relevant to this product</p>
            </div>
            <div>
              <Label>SEO-Friendly URL Slug</Label>
              <Input
                value={editSeo.seo_slug}
                onChange={(e) => setEditSeo(prev => ({ ...prev, seo_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                placeholder="product-name-url-slug"
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setEditProduct(null)}>Cancel</Button>
              <Button onClick={handleSaveSEO} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save SEO
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Discount Codes Management Component
const AdminDiscounts = ({ getAuthHeader }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [newCode, setNewCode] = useState({
    code: "",
    discount_percent: 10,
    customer_name: "",
    customer_email: "",
    notes: ""
  });
  const [batchSettings, setBatchSettings] = useState({
    discount_percent: 10,
    count: 5,
    prefix: "MINI"
  });
  const [includeExpired, setIncludeExpired] = useState(false);
  const [includeUsed, setIncludeUsed] = useState(false);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/discounts/codes?include_expired=${includeExpired}&include_used=${includeUsed}`,
        { headers: getAuthHeader() }
      );
      setCodes(response.data);
    } catch (error) {
      toast.error("Failed to load discount codes");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, includeExpired, includeUsed]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleCreateCode = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/discounts/codes`, newCode, { headers: getAuthHeader() });
      toast.success("Discount code created!");
      setShowCreateModal(false);
      setNewCode({ code: "", discount_percent: 10, customer_name: "", customer_email: "", notes: "" });
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create code");
    }
  };

  const handleGenerateBatch = async () => {
    try {
      const response = await axios.post(
        `${API}/discounts/codes/generate-batch?discount_percent=${batchSettings.discount_percent}&count=${batchSettings.count}&prefix=${batchSettings.prefix}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`Generated ${response.data.created} discount codes!`);
      setShowBatchModal(false);
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate codes");
    }
  };

  const handleDeleteCode = async (codeId) => {
    // Confirm blocked in iframe, proceed directly
    try {
      await axios.delete(`${API}/discounts/codes/${codeId}`, { headers: getAuthHeader() });
      toast.success("Code deleted");
      fetchCodes();
    } catch (error) {
      toast.error("Failed to delete code");
    }
  };

  const handleToggleCode = async (codeId) => {
    try {
      const response = await axios.put(`${API}/discounts/codes/${codeId}/toggle`, {}, { headers: getAuthHeader() });
      toast.success(response.data.is_active ? "Code activated" : "Code deactivated");
      fetchCodes();
    } catch (error) {
      toast.error("Failed to toggle code");
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied: ${code}`);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-xl font-bold" style={{ fontFamily: 'Calibri, sans-serif' }}>
          Discount Codes
        </h2>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setShowBatchModal(true)}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Generate Batch
          </Button>
          <Button onClick={() => setShowCreateModal(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Code
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox 
            checked={includeExpired} 
            onCheckedChange={setIncludeExpired}
          />
          Show expired
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox 
            checked={includeUsed} 
            onCheckedChange={setIncludeUsed}
          />
          Show used
        </label>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : codes.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Tag className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No discount codes yet</p>
          <p className="text-sm mt-2">Create codes for your social media campaigns!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {codes.map((code) => (
            <div 
              key={code.id}
              className={`border rounded-lg p-4 ${
                code.times_used >= code.max_uses 
                  ? "bg-gray-50 opacity-60" 
                  : isExpired(code.expires_at) 
                    ? "bg-red-50 opacity-60"
                    : !code.is_active
                      ? "bg-yellow-50"
                      : "bg-white"
              }`}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  {/* Code with Copy Button */}
                  <div className="flex items-center gap-2">
                    <code className="text-lg font-mono font-bold bg-gray-100 px-3 py-1 rounded">
                      {code.code}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(code.code)}
                      className="h-8 w-8 p-0"
                      title="Copy code"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Discount Badge */}
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold ${
                    code.discount_percent === 25 
                      ? "bg-green-100 text-green-700"
                      : code.discount_percent === 20
                        ? "bg-blue-100 text-blue-700"
                        : "bg-purple-100 text-purple-700"
                  }`}>
                    <Percent className="h-3 w-3" />
                    {code.discount_percent}% OFF
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {code.times_used >= code.max_uses && (
                    <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">USED</span>
                  )}
                  {isExpired(code.expires_at) && (
                    <span className="text-xs bg-red-200 text-red-600 px-2 py-1 rounded">EXPIRED</span>
                  )}
                  {!code.is_active && (
                    <span className="text-xs bg-yellow-200 text-yellow-700 px-2 py-1 rounded">INACTIVE</span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleCode(code.id)}
                    title={code.is_active ? "Deactivate" : "Activate"}
                  >
                    {code.is_active ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteCode(code.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Code Details */}
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
                {code.customer_name && (
                  <span>Customer: {code.customer_name}</span>
                )}
                <span>Min order: £{code.min_order_value.toFixed(2)}</span>
                <span>Expires: {formatDate(code.expires_at)}</span>
                <span>Created: {formatDate(code.created_at)}</span>
                {code.notes && <span className="text-xs italic">Note: {code.notes}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Code Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Discount Code</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCode} className="space-y-4">
            <div>
              <Label>Code (leave blank to auto-generate)</Label>
              <Input
                value={newCode.code}
                onChange={(e) => setNewCode({...newCode, code: e.target.value.toUpperCase()})}
                placeholder="e.g., SUMMER25"
                className="font-mono"
              />
            </div>
            <div>
              <Label>Discount Percentage</Label>
              <Select
                value={newCode.discount_percent.toString()}
                onValueChange={(v) => setNewCode({...newCode, discount_percent: parseInt(v)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10% Off</SelectItem>
                  <SelectItem value="20">20% Off</SelectItem>
                  <SelectItem value="25">25% Off</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Customer Name (optional)</Label>
              <Input
                value={newCode.customer_name}
                onChange={(e) => setNewCode({...newCode, customer_name: e.target.value})}
                placeholder="e.g., John Smith"
              />
            </div>
            <div>
              <Label>Customer Email (optional)</Label>
              <Input
                type="email"
                value={newCode.customer_email}
                onChange={(e) => setNewCode({...newCode, customer_email: e.target.value})}
                placeholder="e.g., john@example.com"
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={newCode.notes}
                onChange={(e) => setNewCode({...newCode, notes: e.target.value})}
                placeholder="e.g., Instagram giveaway winner"
                rows={2}
              />
            </div>
            <div className="bg-gray-50 p-3 rounded text-sm text-muted-foreground">
              <p>• Minimum order: £19.99</p>
              <p>• Single use only</p>
              <p>• Expires in 6 months</p>
            </div>
            <Button type="submit" className="w-full">Create Code</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Batch Generate Modal */}
      <Dialog open={showBatchModal} onOpenChange={setShowBatchModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Batch Codes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Discount Percentage</Label>
              <Select
                value={batchSettings.discount_percent.toString()}
                onValueChange={(v) => setBatchSettings({...batchSettings, discount_percent: parseInt(v)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10% Off</SelectItem>
                  <SelectItem value="20">20% Off</SelectItem>
                  <SelectItem value="25">25% Off</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Number of Codes</Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={batchSettings.count}
                onChange={(e) => setBatchSettings({...batchSettings, count: parseInt(e.target.value) || 5})}
              />
            </div>
            <div>
              <Label>Code Prefix</Label>
              <Input
                value={batchSettings.prefix}
                onChange={(e) => setBatchSettings({...batchSettings, prefix: e.target.value.toUpperCase()})}
                placeholder="MINI"
                className="font-mono"
              />
            </div>
            <div className="bg-gray-50 p-3 rounded text-sm text-muted-foreground">
              <p>• All codes: £19.99 minimum order</p>
              <p>• All codes: Single use only</p>
              <p>• All codes: 6 month expiry</p>
            </div>
            <Button onClick={handleGenerateBatch} className="w-full">
              Generate {batchSettings.count} Codes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// My Design Submissions Management Component
const AdminDesigns = ({ getAuthHeader }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/designs`, { headers: getAuthHeader() });
      setSubmissions(response.data.submissions || []);
    } catch (error) {
      toast.error("Failed to load design submissions");
      console.error("Error fetching design submissions:", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleUpdateStatus = async (designId, newStatus) => {
    try {
      await axios.put(`${API}/designs/${designId}/status?status=${newStatus}`, {}, { headers: getAuthHeader() });
      toast.success(`Status updated to ${newStatus}`);
      fetchSubmissions();
      setSelectedSubmission(null);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'reviewed': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'quoted': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'completed': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const filteredSubmissions = submissions.filter(sub => {
    const matchesStatus = filterStatus === "all" || sub.status === filterStatus;
    const matchesSearch = 
      sub.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Calibri, sans-serif' }}>
            My Design Submissions
          </h2>
          <p className="text-sm text-muted-foreground">
            {submissions.length} total submission{submissions.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button variant="outline" onClick={fetchSubmissions} className="gap-2 rounded-full">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-full"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-48 rounded-full">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="quoted">Quoted</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredSubmissions.length === 0 ? (
        <div className="text-center py-12 bg-secondary/30 rounded-lg">
          <Palette className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No design submissions found</p>
          <p className="text-sm text-muted-foreground mt-1">Submissions will appear here when customers upload their designs</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSubmissions.map((submission) => (
            <div key={submission.id} className="bg-white border border-border rounded-lg overflow-hidden">
              <div 
                className="flex flex-col md:flex-row md:items-center justify-between p-4 cursor-pointer hover:bg-secondary/30 transition-colors"
                onClick={() => setSelectedSubmission(selectedSubmission?.id === submission.id ? null : submission)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Palette className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{submission.name}</p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Mail className="h-3 w-3" />
                      {submission.email}
                      {submission.phone && (
                        <>
                          <Phone className="h-3 w-3 ml-2" />
                          {submission.phone}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-4 md:mt-0">
                  <span className={`text-xs px-3 py-1 rounded-full border capitalize ${getStatusColor(submission.status)}`}>
                    {submission.status || 'pending'}
                  </span>
                  <div className="text-right">
                    <p className="text-sm font-medium">{submission.width} x {submission.height} {submission.unit}</p>
                    <p className="text-xs text-muted-foreground">Qty: {submission.quantity}</p>
                  </div>
                  {selectedSubmission?.id === submission.id ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
              </div>
              
              {selectedSubmission?.id === submission.id && (
                <div className="border-t border-border p-4 bg-secondary/20">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Submission Details */}
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Project Details
                      </h4>
                      <div className="space-y-3 text-sm">
                        <div>
                          <p className="text-muted-foreground">Description:</p>
                          <p className="mt-1 bg-white p-3 rounded border">{submission.description || "No description provided"}</p>
                        </div>
                        {submission.notes && (
                          <div>
                            <p className="text-muted-foreground">Additional Notes:</p>
                            <p className="mt-1 bg-white p-3 rounded border">{submission.notes}</p>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Ruler className="h-4 w-4 text-muted-foreground" />
                          <span>Dimensions: {submission.width} x {submission.height} {submission.unit}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-muted-foreground" />
                          <span>Quantity: {submission.quantity}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span>Submitted: {formatDate(submission.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* File & Actions */}
                    <div>
                      <h4 className="font-medium mb-3">Design File</h4>
                      {submission.file_url ? (
                        <div className="bg-white p-4 rounded border mb-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileText className="h-8 w-8 text-primary" />
                              <div>
                                <p className="font-medium">{submission.original_filename || 'Design File'}</p>
                                <p className="text-xs text-muted-foreground">{submission.filename}</p>
                              </div>
                            </div>
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}${submission.file_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white text-sm rounded-full hover:bg-gray-800 transition-colors"
                            >
                              <Download className="h-4 w-4" />
                              Download
                            </a>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-yellow-50 p-4 rounded border border-yellow-200 mb-4">
                          <p className="text-sm text-yellow-700">No file uploaded</p>
                        </div>
                      )}

                      <h4 className="font-medium mb-3">Update Status</h4>
                      <div className="flex flex-wrap gap-2">
                        {['pending', 'reviewed', 'quoted', 'approved', 'rejected', 'completed'].map((status) => (
                          <Button
                            key={status}
                            variant={submission.status === status ? "default" : "outline"}
                            size="sm"
                            className="rounded-full capitalize"
                            onClick={() => handleUpdateStatus(submission.id, status)}
                          >
                            {status}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Contact Actions */}
                  <div className="mt-6 pt-4 border-t border-border flex flex-wrap gap-3">
                    <a
                      href={`mailto:${submission.email}?subject=Re: Your Custom Design Submission - Mini Stripes UK`}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm rounded-full hover:bg-primary/90 transition-colors"
                    >
                      <Mail className="h-4 w-4" />
                      Email Customer
                    </a>
                    {submission.phone && (
                      <a
                        href={`tel:${submission.phone}`}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm rounded-full hover:bg-green-700 transition-colors"
                      >
                        <Phone className="h-4 w-4" />
                        Call Customer
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Product Importer Component
const AdminImporter = ({ getAuthHeader }) => {
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [skipExisting, setSkipExisting] = useState(true);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [categoriesRes, statusRes, historyRes] = await Promise.all([
        axios.get(`${API}/importer/categories`, { headers: getAuthHeader() }),
        axios.get(`${API}/importer/status`, { headers: getAuthHeader() }),
        axios.get(`${API}/importer/history`, { headers: getAuthHeader() })
      ]);
      setCategories(categoriesRes.data);
      setStatus(statusRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error("Error fetching importer data:", error);
      toast.error("Failed to load importer data");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Poll for status updates when import is running
  useEffect(() => {
    let interval;
    if (status?.is_running) {
      interval = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/importer/status`, { headers: getAuthHeader() });
          setStatus(res.data);
          if (!res.data.is_running) {
            fetchData(); // Refresh history when complete
          }
        } catch (error) {
          console.error("Error polling status:", error);
        }
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [status?.is_running, getAuthHeader, fetchData]);

  const handleCategoryToggle = (slug) => {
    setSelectedCategories(prev => 
      prev.includes(slug) 
        ? prev.filter(s => s !== slug)
        : [...prev, slug]
    );
  };

  const handleSelectAll = () => {
    if (selectedCategories.length === categories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(categories.map(c => c.slug));
    }
  };

  const startImport = async () => {
    try {
      await axios.post(`${API}/importer/start`, {
        categories: selectedCategories,
        skip_existing: skipExisting
      }, { headers: getAuthHeader() });
      toast.success("Import started!");
      // Immediately fetch status
      const res = await axios.get(`${API}/importer/status`, { headers: getAuthHeader() });
      setStatus(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start import");
    }
  };

  const stopImport = async () => {
    try {
      await axios.post(`${API}/importer/stop`, {}, { headers: getAuthHeader() });
      toast.info("Import stopped");
      fetchData();
    } catch (error) {
      toast.error("Failed to stop import");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const progressPercent = status?.total > 0 ? Math.round((status.progress / status.total) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-2xl">Product Importer</h2>
          <p className="text-muted-foreground mt-1">Import products from shop.zengraphics.co.uk</p>
        </div>
        <Button variant="outline" onClick={fetchData} className="rounded-full">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Import Status Card */}
      {status?.is_running && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              <div>
                <h3 className="font-medium text-blue-900">Import in Progress</h3>
                <p className="text-sm text-blue-700">{status.current_item}</p>
              </div>
            </div>
            <Button variant="destructive" size="sm" onClick={stopImport} className="rounded-full">
              <Square className="h-4 w-4 mr-2" />
              Stop
            </Button>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-blue-200 rounded-full h-3 mb-2">
            <div 
              className="bg-blue-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="flex justify-between text-sm text-blue-700">
            <span>{status.progress} / {status.total} products</span>
            <span>{progressPercent}%</span>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="bg-white/50 rounded p-3 text-center">
              <p className="text-2xl font-bold text-green-600">{status.imported_count}</p>
              <p className="text-xs text-muted-foreground">Imported</p>
            </div>
            <div className="bg-white/50 rounded p-3 text-center">
              <p className="text-2xl font-bold text-yellow-600">{status.skipped_count}</p>
              <p className="text-xs text-muted-foreground">Skipped</p>
            </div>
            <div className="bg-white/50 rounded p-3 text-center">
              <p className="text-2xl font-bold text-red-600">{status.error_count}</p>
              <p className="text-xs text-muted-foreground">Errors</p>
            </div>
          </div>
        </div>
      )}

      {/* Last Run Summary */}
      {!status?.is_running && status?.last_run && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <span className="font-medium text-green-900">Last import completed</span>
          </div>
          <p className="text-sm text-green-700 mt-1">
            {new Date(status.last_run).toLocaleString()} — 
            Imported: {status.imported_count}, Skipped: {status.skipped_count}, Errors: {status.error_count}
          </p>
        </div>
      )}

      {/* Category Selection */}
      <div className="bg-white border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg">Select Categories to Import</h3>
          <Button variant="ghost" size="sm" onClick={handleSelectAll}>
            {selectedCategories.length === categories.length ? "Deselect All" : "Select All"}
          </Button>
        </div>
        
        <div className="grid md:grid-cols-2 gap-3">
          {categories.map((category) => (
            <label
              key={category.slug}
              className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                selectedCategories.includes(category.slug) 
                  ? 'border-primary bg-primary/5' 
                  : 'border-border hover:bg-secondary/50'
              }`}
            >
              <Checkbox
                checked={selectedCategories.includes(category.slug)}
                onCheckedChange={() => handleCategoryToggle(category.slug)}
              />
              <div className="flex-1">
                <p className="font-medium">{category.name}</p>
                <p className="text-xs text-muted-foreground truncate">{category.url}</p>
              </div>
            </label>
          ))}
        </div>

        {/* Options */}
        <div className="mt-6 pt-6 border-t border-border">
          <label className="flex items-center gap-3">
            <Checkbox
              checked={skipExisting}
              onCheckedChange={(checked) => setSkipExisting(checked)}
            />
            <div>
              <p className="font-medium">Skip existing products</p>
              <p className="text-sm text-muted-foreground">Don't overwrite products that already exist in the database</p>
            </div>
          </label>
        </div>

        {/* Start Button */}
        <div className="mt-6">
          <Button 
            onClick={startImport} 
            disabled={status?.is_running || selectedCategories.length === 0}
            className="rounded-full w-full md:w-auto"
            size="lg"
          >
            {status?.is_running ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Start Import ({selectedCategories.length} categories)
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Import History */}
      {history && history.recent_imports?.length > 0 && (
        <div className="bg-white border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border bg-secondary/50">
            <h3 className="font-heading font-bold text-lg">
              Recent Imports ({history.total_imported} total)
            </h3>
          </div>
          <div className="divide-y divide-border max-h-64 overflow-y-auto">
            {history.recent_imports.map((item) => (
              <div key={item.id} className="flex items-center justify-between p-3 hover:bg-secondary/30">
                <div>
                  <p className="font-medium text-sm">{item.name}</p>
                  <p className="text-xs text-muted-foreground">{item.category_name}</p>
                </div>
                <span className="text-xs text-muted-foreground">
                  {new Date(item.imported_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Logs */}
      {status?.logs?.length > 0 && (
        <div className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-sm max-h-48 overflow-y-auto">
          {status.logs.map((log, idx) => (
            <div key={idx} className="py-1">
              <span className="text-gray-500">[{idx + 1}]</span> {log}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Admin Loyalty Program Manager Component
const AdminLoyalty = ({ getAuthHeader }) => {
  const [settings, setSettings] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, membersRes] = await Promise.all([
        axios.get(`${API}/loyalty/settings`, { headers: getAuthHeader() }),
        axios.get(`${API}/loyalty/all-members`, { headers: getAuthHeader() })
      ]);
      setSettings(settingsRes.data);
      setMembers(membersRes.data);
    } catch (error) {
      console.error("Error fetching loyalty data:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/loyalty/settings`, settings, {
        headers: getAuthHeader()
      });
      toast.success("Loyalty settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...settings.tiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setSettings({ ...settings, tiers: newTiers });
  };

  const addPointsToMember = async (userId, points, reason) => {
    try {
      await axios.post(`${API}/loyalty/points/add`, null, {
        params: { user_id: userId, points, reason },
        headers: getAuthHeader()
      });
      toast.success(`Added ${points} points`);
      fetchData();
    } catch (error) {
      toast.error("Failed to add points");
    }
  };

  const getTierColor = (tierName) => {
    switch (tierName?.toLowerCase()) {
      case 'platinum': return 'bg-gray-200 text-gray-800';
      case 'gold': return 'bg-yellow-100 text-yellow-800';
      case 'silver': return 'bg-gray-100 text-gray-600';
      default: return 'bg-orange-100 text-orange-800';
    }
  };

  const getMemberTier = (points) => {
    if (!settings?.tiers) return { name: 'Bronze', color: '#CD7F32' };
    let currentTier = settings.tiers[0];
    for (const tier of settings.tiers) {
      if (points >= tier.min_points) currentTier = tier;
    }
    return currentTier;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-loyalty">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center gap-3">
          <Gift className="h-7 w-7" />
          Loyalty Program
        </h2>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'settings' 
              ? 'text-black border-b-2 border-black' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Program Settings
        </button>
        <button
          onClick={() => setActiveTab('members')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'members' 
              ? 'text-black border-b-2 border-black' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Members ({members.length})
        </button>
      </div>

      {activeTab === 'settings' && settings && (
        <div className="space-y-6">
          {/* General Settings */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Settings className="h-5 w-5" />
              General Settings
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label>Points per £1 Spent</Label>
                <Input
                  type="number"
                  value={settings.points_per_pound}
                  onChange={(e) => setSettings({ ...settings, points_per_pound: parseInt(e.target.value) || 1 })}
                  min={1}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Points to £1 Conversion</Label>
                <Input
                  type="number"
                  value={settings.points_to_currency}
                  onChange={(e) => setSettings({ ...settings, points_to_currency: parseInt(e.target.value) || 100 })}
                  min={1}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">{settings.points_to_currency} points = £1</p>
              </div>
              <div>
                <Label>Min Points to Redeem</Label>
                <Input
                  type="number"
                  value={settings.min_redeem_points}
                  onChange={(e) => setSettings({ ...settings, min_redeem_points: parseInt(e.target.value) || 100 })}
                  min={1}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Bonus Points */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Bonus Points
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label>Review Points</Label>
                <Input
                  type="number"
                  value={settings.review_points}
                  onChange={(e) => setSettings({ ...settings, review_points: parseInt(e.target.value) || 0 })}
                  min={0}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Share Points</Label>
                <Input
                  type="number"
                  value={settings.share_points}
                  onChange={(e) => setSettings({ ...settings, share_points: parseInt(e.target.value) || 0 })}
                  min={0}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Referrer Points</Label>
                <Input
                  type="number"
                  value={settings.referral_points_referrer}
                  onChange={(e) => setSettings({ ...settings, referral_points_referrer: parseInt(e.target.value) || 0 })}
                  min={0}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Referee Points</Label>
                <Input
                  type="number"
                  value={settings.referral_points_referee}
                  onChange={(e) => setSettings({ ...settings, referral_points_referee: parseInt(e.target.value) || 0 })}
                  min={0}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Tier Settings */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Crown className="h-5 w-5" />
              Loyalty Tiers
            </h3>
            <div className="space-y-4">
              {settings.tiers?.map((tier, index) => (
                <div 
                  key={index} 
                  className="border border-gray-200 rounded-lg p-4"
                  style={{ borderLeftColor: tier.color, borderLeftWidth: '4px' }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label>Tier Name</Label>
                      <Input
                        value={tier.name}
                        onChange={(e) => updateTier(index, 'name', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Min Points</Label>
                      <Input
                        type="number"
                        value={tier.min_points}
                        onChange={(e) => updateTier(index, 'min_points', parseInt(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Discount %</Label>
                      <Input
                        type="number"
                        value={tier.discount_percent}
                        onChange={(e) => updateTier(index, 'discount_percent', parseInt(e.target.value) || 0)}
                        min={0}
                        max={100}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Color</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          type="color"
                          value={tier.color}
                          onChange={(e) => updateTier(index, 'color', e.target.value)}
                          className="w-14 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={tier.color}
                          onChange={(e) => updateTier(index, 'color', e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label>Benefits (comma separated)</Label>
                    <Input
                      value={tier.benefits?.join(', ') || ''}
                      onChange={(e) => updateTier(index, 'benefits', e.target.value.split(',').map(b => b.trim()))}
                      className="mt-1"
                      placeholder="e.g., 5% discount, Free shipping, Early access"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={saveSettings} disabled={saving} className="rounded-full px-8">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {activeTab === 'members' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Member</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Tier</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Points</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Lifetime</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Spent</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Orders</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {members.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      No loyalty members yet
                    </td>
                  </tr>
                ) : (
                  members.map((member) => {
                    const tier = getMemberTier(member.lifetime_points || 0);
                    return (
                      <tr key={member.user_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-900">{member.user_name || 'Unknown'}</p>
                            <p className="text-xs text-gray-500">{member.user_email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span 
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getTierColor(tier.name)}`}
                          >
                            {tier.name}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          {(member.available_points || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {(member.lifetime_points || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          £{(member.total_spent || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {member.order_count || 0}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" className="text-xs">
                                <Plus className="h-3 w-3 mr-1" />
                                Add Points
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Add Points to {member.user_name}</DialogTitle>
                              </DialogHeader>
                              <form
                                onSubmit={(e) => {
                                  e.preventDefault();
                                  const formData = new FormData(e.target);
                                  addPointsToMember(
                                    member.user_id,
                                    parseInt(formData.get('points')),
                                    formData.get('reason')
                                  );
                                }}
                                className="space-y-4 mt-4"
                              >
                                <div>
                                  <Label>Points to Add</Label>
                                  <Input name="points" type="number" min={1} required className="mt-1" />
                                </div>
                                <div>
                                  <Label>Reason</Label>
                                  <Input name="reason" required className="mt-1" placeholder="e.g., Bonus points, Refund compensation" />
                                </div>
                                <Button type="submit" className="w-full rounded-full">
                                  Add Points
                                </Button>
                              </form>
                            </DialogContent>
                          </Dialog>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Admin Component
export const Admin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, getAuthHeader, loading: authLoading } = useAuth();
  const [stats, setStats] = useState({});
  const [webDesignOpen, setWebDesignOpen] = useState(true);
  const [catalogOpen, setCatalogOpen] = useState(true);
  const [customersOpen, setCustomersOpen] = useState(true);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.is_admin)) {
      navigate("/login");
      return;
    }

    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`, {
          headers: getAuthHeader()
        });
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };

    if (isAuthenticated && user?.is_admin) {
      fetchStats();
    }
  }, [isAuthenticated, authLoading, user, navigate, getAuthHeader]);

  if (authLoading || !isAuthenticated || !user?.is_admin) {
    return (
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-secondary w-1/3" />
            <div className="h-64 bg-secondary" />
          </div>
        </div>
      </div>
    );
  }

  const navItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/admin/live", icon: Activity, label: "Live Activity" },
    { path: "/admin/orders", icon: ShoppingCart, label: "Orders" },
    { path: "/admin/designs", icon: Palette, label: "My Designs" },
    { path: "/admin/importer", icon: Download, label: "Importer" },
    { path: "/admin/config", icon: Settings, label: "Configuration" },
    { path: "/", icon: ExternalLink, label: "Public Store", external: true },
  ];

  const catalogItems = [
    { path: "/admin/products", icon: Package, label: "Products" },
    { path: "/admin/categories", icon: FolderTree, label: "Categories" },
    { path: "/admin/materials", icon: Ruler, label: "Materials" },
    { path: "/admin/shop-pages", icon: Store, label: "Shop Pages" },
    { 
      path: "/admin/website-images", 
      icon: () => (
        <svg viewBox="0 0 18 18" className="h-5 w-5" fill="currentColor" stroke="currentColor" strokeWidth="0.5">
          <rect height="10" width="12" x="3" y="4" fill="none" stroke="currentColor" strokeWidth="1"/>
          <circle cx="6" cy="7" r="1" fill="currentColor"/>
          <polyline points="5 12 5 11 7 9 8 10 11 7 13 9 13 12 5 12" fill="currentColor"/>
        </svg>
      ), 
      label: "File Manager" 
    },
    { path: "/admin/seo", icon: Search, label: "SEO Manager" },
  ];

  const customersItems = [
    { path: "/admin/customers", icon: Users, label: "All Customers" },
    { path: "/admin/loyalty", icon: Gift, label: "Loyalty Program" },
    { path: "/admin/discounts", icon: Tag, label: "Discounts" },
  ];

  const webDesignItems = [
    { path: "/admin/carousel", icon: ImageIcon, label: "Carousel" },
    { path: "/admin/adverts", icon: Megaphone, label: "Shop Adverts" },
    { path: "/admin/tutorials", icon: Play, label: "Video Tutorials" },
  ];

  return (
    <div className="pt-24 pb-16 bg-gray-50 min-h-screen" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="mb-8">
          <p className="label-style mb-2">Admin Panel</p>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Calibri, sans-serif' }}>Mini Stripes UK</h1>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <aside className="md:w-56 shrink-0">
            <nav className="bg-white border border-border rounded-lg overflow-hidden">
              {navItems.map((item, index) => {
                const isActive = !item.external && (location.pathname === item.path || 
                  (item.path !== "/admin" && location.pathname.startsWith(item.path)));
                
                // Insert Catalog section after Live Activity
                const showCatalogAfter = item.label === "Live Activity";
                // Insert Customers section after Orders
                const showCustomersAfter = item.label === "Orders";
                // Insert Web Design section after My Designs
                const showWebDesignAfter = item.label === "My Designs";
                
                if (item.external) {
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-b-0 transition-colors bg-green-50 hover:bg-green-100 text-green-700"
                    >
                      <item.icon className="h-5 w-5" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  );
                }
                
                return (
                  <React.Fragment key={item.path}>
                    <Link
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 border-b border-border transition-colors ${
                        isActive 
                          ? "bg-primary text-primary-foreground" 
                          : "hover:bg-secondary"
                      }`}
                    >
                      <item.icon className="h-5 w-5" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                    
                    {/* Catalog Section */}
                    {showCatalogAfter && (
                      <>
                        <button
                          onClick={() => setCatalogOpen(!catalogOpen)}
                          className="flex items-center justify-between w-full px-4 py-3 border-b border-border transition-colors hover:bg-secondary bg-blue-50"
                        >
                          <div className="flex items-center gap-3">
                            <Package className="h-5 w-5 text-blue-600" />
                            <span className="font-medium text-blue-900">Catalog</span>
                          </div>
                          {catalogOpen ? (
                            <ChevronUp className="h-4 w-4 text-blue-600" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-blue-600" />
                          )}
                        </button>
                        {catalogOpen && catalogItems.map((subItem) => {
                          const subIsActive = location.pathname === subItem.path || 
                            location.pathname.startsWith(subItem.path);
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className={`flex items-center gap-3 px-4 py-3 pl-10 border-b border-border transition-colors ${
                                subIsActive 
                                  ? "bg-primary text-primary-foreground" 
                                  : "hover:bg-secondary bg-blue-50/50"
                              }`}
                            >
                              {typeof subItem.icon === 'function' ? subItem.icon() : <subItem.icon className="h-4 w-4" />}
                              <span className="font-medium text-sm">{subItem.label}</span>
                            </Link>
                          );
                        })}
                      </>
                    )}
                    
                    {/* Customers Section */}
                    {showCustomersAfter && (
                      <>
                        <button
                          onClick={() => setCustomersOpen(!customersOpen)}
                          className="flex items-center justify-between w-full px-4 py-3 border-b border-border transition-colors hover:bg-secondary bg-purple-50"
                        >
                          <div className="flex items-center gap-3">
                            <Users className="h-5 w-5 text-purple-600" />
                            <span className="font-medium text-purple-900">Customers</span>
                          </div>
                          {customersOpen ? (
                            <ChevronUp className="h-4 w-4 text-purple-600" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-purple-600" />
                          )}
                        </button>
                        {customersOpen && customersItems.map((subItem) => {
                          const subIsActive = location.pathname === subItem.path || 
                            location.pathname.startsWith(subItem.path);
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className={`flex items-center gap-3 px-4 py-3 pl-10 border-b border-border transition-colors ${
                                subIsActive 
                                  ? "bg-primary text-primary-foreground" 
                                  : "hover:bg-secondary bg-purple-50/50"
                              }`}
                            >
                              <subItem.icon className="h-4 w-4" />
                              <span className="font-medium text-sm">{subItem.label}</span>
                            </Link>
                          );
                        })}
                      </>
                    )}
                    
                    {/* Web Design Section */}
                    {showWebDesignAfter && (
                      <>
                        <button
                          onClick={() => setWebDesignOpen(!webDesignOpen)}
                          className="flex items-center justify-between w-full px-4 py-3 border-b border-border transition-colors hover:bg-secondary bg-gray-50"
                        >
                          <div className="flex items-center gap-3">
                            <Globe className="h-5 w-5" />
                            <span className="font-medium">Web Design</span>
                          </div>
                          {webDesignOpen ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </button>
                        {webDesignOpen && webDesignItems.map((subItem) => {
                          const subIsActive = location.pathname === subItem.path || 
                            location.pathname.startsWith(subItem.path);
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className={`flex items-center gap-3 px-4 py-3 pl-10 border-b border-border transition-colors ${
                                subIsActive 
                                  ? "bg-primary text-primary-foreground" 
                                  : "hover:bg-secondary bg-gray-50/50"
                              }`}
                            >
                              <subItem.icon className="h-4 w-4" />
                              <span className="font-medium text-sm">{subItem.label}</span>
                            </Link>
                          );
                        })}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </nav>
          </aside>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="bg-white border border-border rounded-lg p-6">
              <Routes>
                <Route path="/" element={<Dashboard stats={stats} getAuthHeader={getAuthHeader} />} />
                <Route path="/live" element={<AdminLiveActivity getAuthHeader={getAuthHeader} />} />
                <Route path="/products" element={<AdminProducts getAuthHeader={getAuthHeader} />} />
                <Route path="/orders" element={<AdminOrders getAuthHeader={getAuthHeader} />} />
                <Route path="/customers" element={<AdminCustomers getAuthHeader={getAuthHeader} />} />
                <Route path="/categories" element={<AdminCategories getAuthHeader={getAuthHeader} />} />
                <Route path="/materials" element={<AdminMaterials getAuthHeader={getAuthHeader} />} />
                <Route path="/shop-pages" element={<AdminShopPages getAuthHeader={getAuthHeader} />} />
                <Route path="/website-images" element={<AdminWebsiteImages getAuthHeader={getAuthHeader} />} />
                <Route path="/adverts" element={<AdminShopAdverts getAuthHeader={getAuthHeader} />} />
                <Route path="/discounts" element={<AdminDiscounts getAuthHeader={getAuthHeader} />} />
                <Route path="/loyalty" element={<AdminLoyalty getAuthHeader={getAuthHeader} />} />
                <Route path="/designs" element={<AdminDesigns getAuthHeader={getAuthHeader} />} />
                <Route path="/carousel" element={<AdminCarousel getAuthHeader={getAuthHeader} />} />
                <Route path="/tutorials" element={<AdminVideoTutorials getAuthHeader={getAuthHeader} />} />
                <Route path="/importer" element={<AdminImporter getAuthHeader={getAuthHeader} />} />
                <Route path="/seo" element={<AdminSEO getAuthHeader={getAuthHeader} />} />
                <Route path="/config/*" element={<AdminConfiguration getAuthHeader={getAuthHeader} />} />
              </Routes>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
